const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export interface LoginRequest {
  email: string;
  password: string;
  /** 6-stelliger TOTP-Code; nur im zweiten Login-Schritt für Konten mit aktivem 2FA. */
  twoFactorCode?: string;
}

export interface LoginResponse {
  access_token: string;
  expires_in: number;
  admin: {
    id: string;
    email: string;
    name: string;
    role: string;
    permissions: string[];
  };
  requiresTwoFactor?: boolean;
  message?: string;
}

/**
 * Antwort von POST /api/admin/auth/refresh. Der Refresh-Token selbst läuft
 * ausschließlich über ein httpOnly-Cookie (F-9) und taucht hier nicht auf.
 */
export interface RefreshTokenResponse {
  access_token: string;
  expires_in: number;
}

export interface ApiError {
  message: string;
  statusCode: number;
  code?: string;
}

export interface AdminProfile {
  id: string;
  email: string;
  name: string;
  role: string;
  permissions: string[];
  isActive: boolean;
  twoFactorEnabled: boolean;
  lastLoginAt?: Date;
  createdAt: Date;
}

/** Roles selectable in the Admin Users management UI. */
export type ManageableAdminRole = 'super_admin' | 'admin';

export interface AdminUserRecord {
  id: string;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  twoFactorEnabled: boolean;
  lastLoginAt?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy?: string | null;
}

export interface CreateAdminUserPayload {
  email: string;
  name: string;
  password: string;
  role: ManageableAdminRole;
}

export interface UpdateAdminUserPayload {
  name?: string;
  role?: ManageableAdminRole;
  isActive?: boolean;
}

export interface BlockedPhoneNumberRecord {
  id: string;
  /** Normalized E.164 form (server-side normalization) */
  phoneNumber: string;
  /** Free-text reason — admin-only field */
  reason?: string;
  createdAt: string;
}

export interface CreateBlockedPhoneNumberPayload {
  /** Any format — server normalizes whitespace/separators away */
  phoneNumber: string;
  reason?: string;
}

/** External-API-Tenant (Partner/Reseller) — `GET /api/admin/tenants`. */
export interface AdminTenant {
  id: string;
  name: string;
  description?: string;
  /** false = pausiert: keine neuen Tokens für Tenant und dessen Teilbaum. */
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Pagination-Metadaten der Tenant-Listen. */
export interface AdminTenantPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/** Seite Tenants samt Pagination. */
export interface AdminTenantPage {
  data: AdminTenant[];
  pagination: AdminTenantPagination;
}

/** Tenant-Zugangsdaten in der Liste — ohne Secret, nur Anzeige-Präfix. */
export interface AdminTenantCredential {
  /** = clientId für POST /v1/auth/token */
  id: string;
  name?: string;
  keyPreview: string;
  /** false = widerrufen (laufende Tokens sofort ungültig). */
  isActive: boolean;
  expiresAt?: string | null;
  lastUsedAt?: string | null;
  createdAt: string;
}

/** Seite Tenant-Zugangsdaten samt Pagination. */
export interface AdminTenantCredentialPage {
  data: AdminTenantCredential[];
  pagination: AdminTenantPagination;
}

/** Frisch ausgestellte Zugangsdaten — `clientSecret` gibt es NUR in dieser Response. */
export interface AdminIssuedTenantCredential {
  clientId: string;
  clientSecret: string;
  name?: string;
  keyPreview: string;
  expiresAt?: string | null;
  createdAt: string;
}

/** Response von `POST /api/admin/tenants`. */
export interface AdminTenantCreated {
  tenant: AdminTenant;
  initialCredential?: AdminIssuedTenantCredential;
}

/** Body von `POST /api/admin/tenants`. */
export interface CreateAdminTenantPayload {
  name: string;
  description?: string;
  createInitialCredential?: boolean;
}

/** Body von `POST /api/admin/tenants/:id/credentials`. */
export interface CreateAdminTenantCredentialPayload {
  name?: string;
  /** ISO 8601, muss in der Zukunft liegen. */
  expiresAt?: string;
}

/** Such- und Pagination-Parameter der Tenant-Liste. */
export interface AdminTenantListParams {
  search?: string;
  page?: number;
  limit?: number;
}

/** Ein Kunde in der Tenant-Zuordnungsliste — `GET /api/admin/tenants/customer-assignments`. */
export interface AdminTenantAssignmentCustomer {
  id: string;
  name: string;
  email?: string;
  isActive: boolean;
  /** Aktueller Tenant des Kunden; null = keinem Tenant zugeordnet (Default-Tenant/direkt verwaltet). */
  tenantId: string | null;
  /** Anzeigename des aktuellen Tenants; null passend zu `tenantId`. */
  tenantName: string | null;
}

/** Seite Kunden-Tenant-Zuordnungen samt Pagination. */
export interface AdminTenantAssignmentPage {
  data: AdminTenantAssignmentCustomer[];
  pagination: AdminTenantPagination;
}

/** Such-, Filter- und Pagination-Parameter der Tenant-Zuordnungsliste. */
export interface AdminTenantAssignmentListParams {
  search?: string;
  /**
   * Pflichtfeld — es wird immer genau eine Quelle gefiltert (nie "alle Tenants").
   * UUID eines Tenants, oder der Sentinel `'unassigned'` für Kunden ohne Tenant.
   */
  tenantId: string;
  page?: number;
  limit?: number;
}

/** Body von `PATCH /api/admin/tenants/customer-assignments`. */
export interface ReassignCustomersToTenantPayload {
  /** Max. 200 IDs je Aufruf. */
  customerIds: string[];
  targetTenantId: string;
}

/** Response von `PATCH /api/admin/tenants/customer-assignments`. */
export interface ReassignCustomersToTenantResult {
  movedCount: number;
  targetTenantId: string;
}

/** A single entry in the CID reserved-number pool. */
export interface ReservedCidRecord {
  /** E.164 phone number, e.g. "+4930123456". */
  phonenumber: string;
  /** Optional admin comment stored with the reservation. */
  comment?: string;
  /** Whether this number is currently assigned to a bot. */
  taken: boolean;
  /** foreign_id of the bot this number is assigned to — only set when taken = true. */
  takenBy?: string;
}

/** Response returned by CID pool import endpoints. */
export interface CidPoolImportResult {
  /** Number of entries successfully imported. */
  imported: number;
  /** Phone numbers that were skipped (already existed). */
  skipped: string[];
}

/** Payload for a single entry in the reserved-CID bulk import. */
export interface ImportReservedCidPayload {
  /** E.164 phone number. */
  phonenumber: string;
  /** Mandatory comment explaining why this number is reserved. */
  comment: string;
}

// ===== ERROR EVENTS (ADMIN) — Seite „Error Events", Super-Admin only =====

/** Dringlichkeit eines Fehler-Events — vom Error-Processor anhand `severity_mappings` bestimmt, nicht vom Sender. */
export type ErrorEventSeverity = 'critical' | 'very_high' | 'high' | 'medium' | 'low';

/** Bearbeitungsstatus im Admin-Dashboard. Übergänge: `open` → `acknowledged` → `resolved`. */
export type ErrorEventStatus = 'open' | 'acknowledged' | 'resolved';

/** Ein Fehler-Event, wie es die Seite „Error Events" anzeigt. */
export interface ErrorEvent {
  id: string;
  service: string;
  errorIdentifier: string;
  /** Bot, bei dem der Fehler auftrat — `null`, wenn der Sender keinen mitgeschickt hat. */
  agentId: string | null;
  /** Mandant — `null` bei nicht zuordenbaren Events. */
  customerId: string | null;
  sessionId: string | null;
  errorSource: string | null;
  error: string;
  info: string;
  help: string;
  severity: ErrorEventSeverity;
  status: ErrorEventStatus;
  /** Zeitpunkt, den der Sender gemeldet hat. */
  occurredAt: string;
  /** Zeitpunkt der Speicherung durch den Error-Processor. */
  createdAt: string;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
}

/** Server-seitig paginierte Event-Liste, neueste zuerst. */
export interface ErrorEventListResponse {
  items: ErrorEvent[];
  total: number;
  page: number;
  limit: number;
}

/** UND-verknüpfte Filter — gemeinsam von der Liste und den Bulk-Aktionen genutzt. */
export interface ErrorEventFilter {
  service?: string;
  errorIdentifier?: string;
  customerId?: string;
  severity?: ErrorEventSeverity;
  status?: ErrorEventStatus;
  /** Untere Zeitgrenze (inklusive), ISO-8601, gegen `created_at`. */
  from?: string;
  /** Obere Zeitgrenze (inklusive), ISO-8601, gegen `created_at`. */
  to?: string;
}

/** Filter plus Paginierung für {@link ApiService.getErrorEvents}. */
export interface ErrorEventListParams extends ErrorEventFilter {
  page?: number;
  limit?: number;
}

/**
 * Auswahlkriterium der Bulk-Aktionen — die Listen-Filter plus optionale
 * explizite Event-IDs (Checkbox-Auswahl in der Tabelle), UND-verknüpft.
 */
export interface BulkErrorEventSelection extends ErrorEventFilter {
  /** Nur diese Events — darf nicht leer sein, wenn gesetzt. */
  ids?: string[];
}

/** Antwort der Bulk-Aktionen `/bulk-acknowledge` und `/bulk-resolve` — wirken auf ALLE Events der Auswahl. */
export interface BulkErrorEventActionResponse {
  updated: number;
}

/** Body für `POST /api/admin/error-events/test` — löst ein synthetisches Event auf `service-errors` aus. */
export interface CreateTestErrorEventRequest {
  service: string;
  errorIdentifier: string;
  customerId?: string;
  agentId?: string;
  sessionId?: string;
  errorSource?: string;
  error: string;
  info: string;
  help: string;
}

export interface CreateTestErrorEventResponse {
  published: boolean;
}

/** Kerndaten des jüngsten offenen Events — Inhalt der Desktop-Benachrichtigung. */
export interface LatestOpenErrorEvent {
  service: string;
  errorIdentifier: string;
  severity: ErrorEventSeverity;
  /** Vergleichsanker für „neu seit letztem Poll" (ISO-8601). */
  createdAt: string;
}

/**
 * Antwort von `GET /api/admin/error-events/summary` — kompakter Zustand für
 * die Benachrichtigungs-Glocke in der Kopfzeile (Poll beim Laden + alle 30 s).
 */
export interface ErrorEventNotificationSummary {
  /** Anzahl aller Events im Status `open`. */
  openTotal: number;
  /** Offene Events je Dringlichkeit — enthält immer alle fünf Stufen, ohne Events als `0`. */
  openBySeverity: Record<ErrorEventSeverity, number>;
  /** Jüngstes offenes Event — `null`, wenn kein Event offen ist. */
  latestOpenEvent: LatestOpenErrorEvent | null;
  /** DLQ-Tiefe — `null`, wenn die RabbitMQ-Management-API gerade nicht erreichbar ist. */
  dlqTotal: number | null;
}

/** Gründe, aus denen eine Nachricht in der Dead-Letter-Queue landet. */
export type DlqReason = 'invalid_json' | 'schema_violation' | 'processing_failed';

/** Eine Nachricht in `service-errors-dlq` — TTL 14 Tage. */
export interface DlqMessage {
  id: string;
  reason: DlqReason;
  /** `undefined`, wenn der Payload gar nicht als JSON-Objekt mit `service`-Feld lesbar war. */
  service?: string;
  errorIdentifier?: string;
  /** Vollständiger Roh-Payload der Nachricht. */
  payload: string;
  /** Gekürzte Anzeige-Variante des Payloads für die Tabelle. */
  payloadPreview: string;
  deadLetteredAt?: string;
  /** Anzahl der Zustellversuche vor dem Dead-Lettering. */
  retryCount: number;
}

export interface DlqListResponse {
  messages: DlqMessage[];
  total: number;
}

export interface DlqRequeueResponse {
  requeued: number;
}

export interface DlqDeleteResponse {
  deleted: boolean;
}

// ===== ERROR EVENTS — SEVERITY-KONFIGURATION (ADMIN) =====
// Basis: /api/admin/error-events/severity-config. Bestimmt, welche Dringlichkeit
// NEU eintreffende Events bekommen — wirkt nie rückwirkend auf gespeicherte Events.

/**
 * Fachliche Bündelung mehrerer `error_identifier` mit gleicher betrieblicher
 * Dringlichkeit (z.B. „Hooks & Nachlauf"). Trägt die Default-Severity, die alle
 * Mitglieder ohne eigene Identifier-Regel erben.
 */
export interface SeverityGroup {
  id: string;
  name: string;
  description: string | null;
  /** Anzahl der Katalog-Einträge mit dieser `groupId` — serverseitig gezählt. */
  memberCount: number;
  /** Severity der globalen Gruppen-Regel — `null`, wenn für die Gruppe global keine Regel existiert. */
  globalSeverity: ErrorEventSeverity | null;
}

/** Body für `POST /severity-config/groups`. Server antwortet 409 bei doppeltem Namen. */
export interface CreateSeverityGroupRequest {
  name: string;
  description?: string;
}

/** Body für `PATCH /severity-config/groups/:id` — nur gesetzte Felder werden geändert. */
export interface UpdateSeverityGroupRequest {
  name?: string;
  description?: string | null;
}

/**
 * Herkunft eines Identifiers: `registered` = im Code-Katalog deklariert,
 * `observed` = nur im Betrieb aufgetaucht und nachträglich erfasst.
 */
export type SeverityCatalogSource = 'registered' | 'observed';

/** Ein bekannter `error_identifier` samt Gruppen-Zuordnung. */
export interface SeverityCatalogEntry {
  id: string;
  errorIdentifier: string;
  /** Services, die diesen Identifier bereits gesendet haben — leer bei registrierten, nie beobachteten Identifiern. */
  services: string[];
  source: SeverityCatalogSource;
  description: string | null;
  /** `null` = keiner Gruppe zugeordnet, fällt damit auf die Identifier-Regel bzw. den Rückfallwert zurück. */
  groupId: string | null;
}

/**
 * Regel auf Identifier-Ebene.
 *
 * Optional zusätzlich service-scoped: `service: null` ist die Wildcard und gilt
 * für den Identifier aus jedem Service; ein gesetzter `service` verfeinert die
 * Regel auf genau diesen Sender und sticht die Wildcard.
 */
export interface SeverityIdentifierRule {
  id: string;
  /** `null` = globale Regel, sonst kundenspezifischer Override. */
  customerId: string | null;
  /** `null` = gilt für alle Services (Wildcard). */
  service: string | null;
  errorIdentifier: string;
  severity: ErrorEventSeverity;
}

/** Regel auf Gruppen-Ebene — greift für alle Mitglieder ohne eigene Identifier-Regel. */
export interface SeverityGroupRule {
  id: string;
  /** `null` = globale Regel, sonst kundenspezifischer Override. */
  customerId: string | null;
  groupId: string;
  severity: ErrorEventSeverity;
}

/** Antwort von `GET /severity-config/rules` — ohne `customerId` die globalen Regeln. */
export interface SeverityRulesResponse {
  identifierRules: SeverityIdentifierRule[];
  groupRules: SeverityGroupRule[];
}

/** Body für `PUT /severity-config/rules/identifier` — Upsert je (customerId, service, errorIdentifier). */
export interface UpsertSeverityIdentifierRuleRequest {
  /** Weglassen oder `null` für die globale Regel. */
  customerId?: string | null;
  /** Weglassen oder `null` für die Service-Wildcard — die Regel gilt dann für jeden Sender. */
  service?: string | null;
  errorIdentifier: string;
  severity: ErrorEventSeverity;
}

/** Body für `PUT /severity-config/rules/group` — Upsert je (customerId, groupId). */
export interface UpsertSeverityGroupRuleRequest {
  /** Weglassen oder `null` für die globale Regel. */
  customerId?: string | null;
  groupId: string;
  severity: ErrorEventSeverity;
}

export interface UpdateProfileRequest {
  name?: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

/** Antwort von POST /api/admin/auth/2fa/setup. */
export interface Enable2FAResponse {
  /** QR-Code als Data-URI (direkt als `<img src>` nutzbar). */
  qrCode: string;
  /** Base32-Secret für die manuelle Eingabe in der Authenticator-App. */
  secret: string;
  /** @deprecated Backup-Codes werden nicht mehr unterstützt; das Backend liefert ein leeres Array. */
  backupCodes?: string[];
}

/** Antwort von GET /api/admin/auth/2fa/status. */
export interface TwoFactorStatus {
  enabled: boolean;
  /** ISO-Zeitstempel der Aktivierung (nur bei `enabled`). */
  enabledAt?: string;
}

/** Antwort von POST /api/admin/users/{id}/2fa/reset. */
export interface ResetTwoFactorResponse {
  message: string;
  /** false, wenn die Benachrichtigungs-E-Mail an den Nutzer nicht versendet werden konnte. */
  notificationSent: boolean;
}

export interface SystemStatus {
  services: Array<{
    name: string;
    displayName: string;
    status: 'operational' | 'maintenance' | 'down';
    statusColor: 'green' | 'yellow' | 'red';
    bgColor: string;
    details?: any;
  }>;
  overall: 'healthy' | 'degraded' | 'down';
}

export interface TelephonyStatus {
  freeCids: number;
  warning: boolean;
  warningThreshold: number;
  status: 'healthy' | 'warning' | 'error';
  message: string;
}

export interface DashboardMetrics {
  activeCustomers: number;
  monthlyConversations: number;
  totalCallMinutes: number;
  activeLlmModels: number;
  apiCallsToday: number;
  lastUpdated: string;
}

export type MetricType = 'activeCustomers' | 'monthlyConversations' | 'totalCallMinutes' | 'activeLlmModels' | 'apiCallsToday';
export type TimeRange = 'day' | 'week' | 'month';

export interface MetricsHistoryPoint {
  timestamp: string;
  value: number;
}

export interface MetricsHistoryResponse {
  metric: MetricType;
  range: TimeRange;
  data: MetricsHistoryPoint[];
}

// Call Volume (Anrufvolumen) dashboard
export type CallChannel = 'voice' | 'chat' | 'combined';

export interface CallVolumeDayPoint {
  /** Calendar day, `YYYY-MM-DD`. */
  date: string;
  /** Number of sessions that started on this day. */
  count: number;
  /** Peak number of simultaneously active sessions on this day. */
  maxParallel: number;
}

export interface CallVolumeChannelData {
  /** Total sessions in the month. */
  total: number;
  /** One entry per calendar day of the month (missing days are zeroed). */
  days: CallVolumeDayPoint[];
}

export interface CallVolumeResponse {
  /** Selected month, `YYYY-MM`. */
  month: string;
  voice: CallVolumeChannelData;
  chat: CallVolumeChannelData;
  combined: CallVolumeChannelData;
}

export interface Customer {
  id: string;
  name: string;
  description?: string;
  /** E-Mail des Owner-Users. Wird in der Kundenübersicht angezeigt und ist durchsuchbar. */
  ownerEmail?: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  stats: {
    activeChatbots: number;
    activeVoicebots: number;
    phoneNumbers: number;
    callMinutesTotal: number;
    callMinutesLastMonth: number;
  };
  subscription?: {
    planName: string;
    status: string;
    usedCallMinutes: number;
    usedCallSeconds: number;
    maxCallMinutes: number | null;
    billingAddress?: {
      company: string;
      name: string;
      street: string;
      zip: string;
      city: string;
      country: string;
    };
  };
}

export interface CustomerListResponse {
  customers: Customer[];
  total: number;
  page: number;
  limit: number;
}

export interface CustomerListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'inactive';
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  /** Globale Tenant-Auswahl; filtert exakt auf diesen Tenant (auch beim Default-Tenant). */
  tenantId?: string;
}

export interface CreateCustomerRequest {
  name: string;
  description?: string;
  isActive?: boolean;
}

export interface UpdateCustomerRequest {
  name?: string;
  description?: string;
  isActive?: boolean;
}

export interface InitiateCustomerCreationRequest {
  companyName: string;
  description?: string;
  ownerEmail: string;
  ownerName: string;
  ownerPhoneNumber?: string;
  registrationProfileId: string;
}

export interface InitiateCustomerCreationResponse {
  message: string;
  ownerEmail: string;
  companyName: string;
  expiresAt: Date;
  pendingRegistrationId: string;
}

export interface CreateCustomerDirectlyRequest {
  companyName: string;
  description?: string;
  ownerEmail: string;
  ownerName: string;
  ownerPhoneNumber?: string;
  registrationProfileId: string;
}

export interface CreateCustomerDirectlyResponse {
  message: string;
  customerName: string;
  customerId: string;
  ownerEmail: string;
  passwordSent: boolean;
}

export interface RegistrationProfile {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  isDefault: boolean;
  sortOrder: number;
}

export interface StartImpersonationRequest {
  customerId: string;
  reason?: string;
}

export interface ImpersonationResponse {
  token: string;
  expiresIn: number;
  /** `null` bei Kunden ohne Portal-Benutzer (CA-759). */
  customerEmail: string | null;
  sessionId: string;
}

/**
 * Identitäts-Slice eines Agents aus dem Admin-Endpoint (kein Mandanten-Filter).
 * Dient dazu, aus einer Agent-ID den zugehörigen Customer für die
 * Chat-Link-Impersonation aufzulösen.
 */
export interface AdminAgentIdentity {
  id: string;
  name: string;
  type: string;
  status: string;
  customerId: string;
  /** Dem Agent zugeordnete Rufnummer, sofern vorhanden — nur zur Anzeige. */
  phoneNumber?: string;
}

/**
 * Identitäts-Slice eines Knowledge-Verzeichnisses aus dem Admin-Endpoint.
 * Dient dazu, aus einem Knowledge-Link den zugehörigen Customer für die
 * Impersonation aufzulösen.
 */
export interface AdminKnowledgeDirectoryIdentity {
  id: string;
  name: string;
  customerId: string;
}

export interface CustomerPlatformConfig {
  id: string;
  customerId: string;
  showChatbots: boolean;
  showVoicebots: boolean;
  showKnowledge: boolean;
  showTemplates: boolean;
  showTools: boolean;
  showTestScenarios: boolean;
  showUserManagement: boolean;
  showVoiceFunctionality: boolean;
  showSpeechToText: boolean;
  showTimeoutsConfig: boolean;
  showFeedbackFunctionality: boolean;
  showApiKeysManagement: boolean;
  /** Retention period in days (from call end) after which conversations are auto-anonymized. */
  dataRetentionDays: number;
  /** Whether the automatic data-anonymization job is enabled for this customer. */
  dataAnonymizationEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdatePlatformConfigRequest {
  showChatbots?: boolean;
  showVoicebots?: boolean;
  showKnowledge?: boolean;
  showTemplates?: boolean;
  showTools?: boolean;
  showTestScenarios?: boolean;
  showUserManagement?: boolean;
  showVoiceFunctionality?: boolean;
  showSpeechToText?: boolean;
  showTimeoutsConfig?: boolean;
  showFeedbackFunctionality?: boolean;
  showApiKeysManagement?: boolean;
  dataRetentionDays?: number;
  dataAnonymizationEnabled?: boolean;
}

export type LLMParameterType = 'string' | 'int' | 'float' | 'bool' | 'enum' | 'array';

export interface LLMParameterDefinition {
  name: string;
  type: LLMParameterType;
  nullable: boolean;
  min?: number | null;
  max?: number | null;
  default?: string | number | boolean | Array<string | number | boolean> | null;
  description?: string;
  /** Allowed choices for `enum` type (single choice). */
  allowedValues?: Array<string | number>;
  /** Element type for `array` type. */
  itemType?: 'string' | 'int' | 'float';
  /** Kundensichtbarkeit (CA-627): `false` = nur Admin-Flächen/E1, nicht in der Kunden-Agent-Maske; fehlend = sichtbar. */
  isCustomerVisibleParameter?: boolean;
}

export type LLMProviderDriverType = 'openai' | 'azure-openai' | 'openai-compatible' | 'anthropic' | 'grok';

export interface LLMProvider {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  baseUrl?: string;
  /** Runtime driver class (CA-452). */
  driverType?: LLMProviderDriverType;
  /** Region tag, e.g. 'eu' / 'us'. */
  dataResidency?: string;
  /** True if a provider-level API key is stored (the value itself is never returned). */
  hasApiKey?: boolean;
  /** @deprecated Historisches Built-in-Flag; Backend entfernt das Feld im Folge-Release. */
  isBuiltIn?: boolean;
  apiKeyRequired: boolean;
  status: 'active' | 'inactive' | 'deprecated';
  configuration?: {
    supportedParameters?: LLMParameterDefinition[];
    /** Azure OpenAI REST API version (only for azure-openai driver). */
    apiVersion?: string;
    /** @deprecated Use supportedParameters with default field instead */
    defaultMaxTokens?: number;
    /** @deprecated Use supportedParameters with default field instead */
    defaultTemperature?: number;
    rateLimits?: {
      requestsPerMinute?: number;
      tokensPerMinute?: number;
    };
    [key: string]: any;
  };
  createdAt: string;
  updatedAt: string;
  modelsCount?: number;
}

export interface CreateLLMProviderRequest {
  name: string;
  displayName: string;
  description?: string;
  baseUrl?: string;
  driverType?: LLMProviderDriverType;
  dataResidency?: string;
  /** Plaintext API key, write-only — stored encrypted, never returned. */
  apiKey?: string;
  /** Azure OpenAI REST API version. */
  apiVersion?: string;
  apiKeyRequired?: boolean;
  configuration?: any;
}

export interface UpdateLLMProviderRequest {
  name?: string;
  displayName?: string;
  description?: string;
  baseUrl?: string;
  driverType?: LLMProviderDriverType;
  dataResidency?: string;
  /** Plaintext API key, write-only — stored encrypted, never returned. */
  apiKey?: string;
  /** Azure OpenAI REST API version. */
  apiVersion?: string;
  apiKeyRequired?: boolean;
  status?: 'active' | 'inactive' | 'deprecated';
  configuration?: any;
}

export interface LLMModel {
  id: string;
  providerId: string;
  modelKey: string;
  displayName: string;
  description?: string;
  status: 'active' | 'inactive' | 'deprecated' | 'beta';
  supportsTools: boolean;
  supportsVision: boolean;
  supportsStreaming: boolean;
  supportsSystemMessages: boolean;
  maxTokens?: number;
  contextWindow?: number;
  inputCostPerMillionTokensEur?: number;
  outputCostPerMillionTokensEur?: number;
  /** Preis pro 1 Mio. cache-bedienter Input-Tokens (EUR); null → voller Input-Preis. */
  cachedInputCostPerMillionTokensEur?: number;
  minTemperature?: number;
  maxTemperature?: number;
  maxOutputTokens?: number;
  modelFamily?: string;
  version?: string;
  releaseDate?: Date;
  deprecationDate?: Date;
  additionalConfig?: {
    supportedParameters?: LLMParameterDefinition[];
    unsupportedParameters?: string[];
    /** Reasoning-Modell (CA-825): OpenAI/Azure-Treiber nutzen die Responses-API; ungesetzt → Erkennung am Modellnamen. */
    reasoningModel?: boolean;
    supportedFormats?: string[];
    specialCapabilities?: string[];
    limitations?: string[];
    recommendedUseCases?: string[];
  };
  createdAt: Date;
  updatedAt: Date;
  provider?: LLMProvider;
  fullModelName: string;
  isAvailable: boolean;
}

export interface CreateModelRequest {
  providerId: string;
  modelKey: string;
  displayName: string;
  description?: string;
  supportsTools?: boolean;
  supportsVision?: boolean;
  supportsStreaming?: boolean;
  supportsSystemMessages?: boolean;
  maxTokens?: number;
  contextWindow?: number;
  inputCostPerMillionTokensEur?: number;
  outputCostPerMillionTokensEur?: number;
  /** Preis pro 1 Mio. cache-bedienter Input-Tokens (EUR); weglassen → voller Input-Preis. */
  cachedInputCostPerMillionTokensEur?: number;
  minTemperature?: number;
  maxTemperature?: number;
  maxOutputTokens?: number;
  modelFamily?: string;
  version?: string;
  releaseDate?: string;
  deprecationDate?: string;
  additionalConfig?: {
    supportedParameters?: LLMParameterDefinition[];
    unsupportedParameters?: string[];
    /** Reasoning-Modell (CA-825): OpenAI/Azure-Treiber nutzen die Responses-API; ungesetzt → Erkennung am Modellnamen. */
    reasoningModel?: boolean;
    supportedFormats?: string[];
    specialCapabilities?: string[];
    limitations?: string[];
    recommendedUseCases?: string[];
  };
}

export interface UpdateModelRequest extends Partial<CreateModelRequest> {
  status?: 'active' | 'inactive' | 'deprecated' | 'beta';
}

export interface ModelListParams {
  includeInactive?: boolean;
  provider?: string;
}

// Customer Model Access Types - matches API response structure
export interface CustomerModelAccess {
  id: string;
  modelKey: string;
  displayName: string;
  description: string;
  provider: {
    name: string;
    displayName: string;
  };
  capabilities: {
    supportsTools: boolean;
    supportsVision: boolean;
    supportsStreaming: boolean;
    supportsSystemMessages: boolean;
  };
  limits: {
    monthlyTokenLimit?: number;
    monthlyRequestLimit?: number;
    currentMonthTokens: number;
    currentMonthRequests: number;
  };
  cost: {
    inputCostPerMillionTokensEur: string;
    outputCostPerMillionTokensEur: string;
  };
  permissions?: any;
  status: string; // 'active', 'inactive', etc.
}

export interface GrantModelAccessRequest {
  modelIds: string[];
  options?: {
    monthlyTokenLimit?: number;
    monthlyRequestLimit?: number;
    dailyTokenLimit?: number;
    dailyRequestLimit?: number;
    maxMonthlyCost?: number;
    permissions?: {
      canUseTools?: boolean;
      canStream?: boolean;
      maxTemperature?: number;
      maxTokensPerRequest?: number;
      allowedFeatures?: string[];
      restrictedFeatures?: string[];
    };
    reason?: string;
    expiresAt?: Date;
  };
}

export interface RevokeModelAccessRequest {
  modelIds: string[];
  reason?: string;
}

export interface CustomerUsageStats {
  totalTokensUsed: number;
  totalRequestsMade: number;
  totalCost: number;
  modelBreakdown: Array<{
    modelKey: string;
    providerName: string;
    tokensUsed: number;
    requestsMade: number;
    cost: number;
  }>;
}

// ===== LANGUAGE CONFIGURATION INTERFACES =====

export interface LanguageDto {
  code: string;
  label: string;
  isActive: boolean;
  sortOrder: number;
}

export interface LanguageConfigResponse {
  languages: LanguageDto[];
  defaultLanguage: string;
  activeLanguages: LanguageDto[];
  inactiveLanguages: LanguageDto[];
}

export interface ToggleLanguageRequest {
  code: string;
  isActive: boolean;
}

export interface TemplateTranslationStatus {
  templateId: string;
  templateName: string;
  availableLanguages: string[];
  missingLanguages: string[];
  isFullyTranslated: boolean;
}

export interface TranslationSummary {
  totalTemplates: number;
  fullyTranslatedCount: number;
  incompleteCount: number;
  activeLanguages: string[];
  incompleteTemplates: TemplateTranslationStatus[];
}

// ===== FILLER CONFIGURATION TYPES =====

export interface FillerConfig {
  provider: string;
  model: string;
  prompt: string;
  temperature: number;
  maxTokens: number;
  enabled: boolean;
}

export interface UpdateFillerConfigRequest {
  provider?: string;
  model?: string;
  prompt?: string;
  temperature?: number;
  maxTokens?: number;
  enabled?: boolean;
}

/**
 * Wert eines konfigurierten Modell-Parameters (CA-582) — Shape identisch zum
 * Backend-`LLMParameterValue` (Skalar, Skalar-Array oder `null`).
 */
export type LLMParameterValue = string | number | boolean | Array<string | number | boolean> | null;

/** Background-Job-LLM-Konfiguration der Hook-Generierung. `null` = unkonfiguriert. */
export interface HookLlmConfig {
  provider: string | null;
  model: string | null;
  /** Konfigurierte Modell-Parameter (CA-582) oder `null` = Code-Defaults. */
  llmParameters: Record<string, LLMParameterValue> | null;
}

export interface UpdateHookLlmConfigRequest {
  provider?: string;
  model?: string;
  /** Ersetzt den gespeicherten Parameter-Satz komplett (leeres Objekt = löschen). */
  llmParameters?: Record<string, LLMParameterValue>;
}

/**
 * Background-Job-LLM-Konfiguration der Audio-Transkription (Whisper-Diktat,
 * CA-427). `null` = unkonfiguriert (Diktat schlägt mit Fehlermeldung fehl).
 */
export interface TranscriptionLlmConfig {
  provider: string | null;
  /** OpenAI: Modellname (whisper-1). Azure OpenAI: Whisper-Deployment-Name. */
  model: string | null;
}

/** Partial-Update der Transkriptions-LLM-Konfiguration. */
export interface UpdateTranscriptionLlmConfigRequest {
  provider?: string;
  model?: string;
}

/** Background-Job-LLM-Konfiguration der KI-Werkzeuge. `null` = unkonfiguriert. */
export interface InternalAiLlmConfig {
  provider: string | null;
  model: string | null;
  /** Konfigurierte Modell-Parameter (CA-582) oder `null` = Request-Werte der Werkzeuge. */
  llmParameters: Record<string, LLMParameterValue> | null;
}

export interface UpdateInternalAiLlmConfigRequest {
  provider?: string;
  model?: string;
  /** Ersetzt den gespeicherten Parameter-Satz komplett (leeres Objekt = löschen). */
  llmParameters?: Record<string, LLMParameterValue>;
}

/**
 * Background-Job-LLM-Konfiguration der Hook-Evaluation (Post-Call-Auswertung
 * von Conditional-Hook-Bedingungen, CA-450). `null` = unkonfiguriert
 * (Auswertung schlägt mit Fehlermeldung fehl).
 */
export interface HookEvaluationLlmConfig {
  provider: string | null;
  model: string | null;
  /** Konfigurierte Modell-Parameter (CA-582) oder `null` = Agent-basierte Sampling-Werte. */
  llmParameters: Record<string, LLMParameterValue> | null;
}

/** Partial-Update der Hook-Evaluation-LLM-Konfiguration. */
export interface UpdateHookEvaluationLlmConfigRequest {
  provider?: string;
  model?: string;
  /** Ersetzt den gespeicherten Parameter-Satz komplett (leeres Objekt = löschen). */
  llmParameters?: Record<string, LLMParameterValue>;
}

export interface RateLimitDefaults {
  messageRateLimit: number;
  sessionCreationLimit: number;
  concurrentSessionLimit: number;
}

export interface UpdateRateLimitDefaultsRequest {
  messageRateLimit?: number;
  sessionCreationLimit?: number;
  concurrentSessionLimit?: number;
}

// ===== RATE LIMIT VIOLATION INTERFACES =====

export type ViolationType = 'message_rate' | 'session_creation' | 'concurrent_session';

export interface RateLimitViolation {
  id: string;
  ip: string;
  agentId?: string;
  agentName?: string;
  customerId?: string;
  customerName?: string;
  violationType: ViolationType;
  count: number;
  limit: number;
  timestamp: string;
  createdAt: string;
}

export interface RateLimitViolationListResponse {
  items: RateLimitViolation[];
  total: number;
  page: number;
  limit: number;
}

export interface RateLimitViolationStats {
  total: number;
  byType: Record<string, number>;
  topIps: Array<{ ip: string; count: number }>;
}

export interface RateLimitViolationParams {
  customerId?: string;
  agentId?: string;
  violationType?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

// ===== ADMIN AGENT INTERFACES =====

export interface AdminAgent {
  id: string;
  name: string;
  type: 'chat' | 'voice';
  customerId: string;
  /** Customer name, joined backend-side — null only if the customer record is missing. */
  customerName?: string | null;
  isActive: boolean;
  rateLimitConfig?: {
    messageRateLimit?: number;
    sessionCreationLimit?: number;
    concurrentSessionLimit?: number;
  };
  /** SMS sender ID / originator for this agent. Overrides provider-level originator when set. Null means fallback to provider or 'CHATBOT'. */
  smsSenderId?: string | null;
  /** Phone number (CID) currently assigned to this voice agent; null when none is assigned. */
  phoneNumber?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Pagination metadata returned by GET /api/admin/agents. */
export interface AdminAgentsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface UpdateAgentRateLimitsRequest {
  messageRateLimit?: number | null;
  sessionCreationLimit?: number | null;
  concurrentSessionLimit?: number | null;
}

// Prompt Template Types
export enum PromptTemplateCategory {
  GENERAL = 'general',
  CUSTOMER_SUPPORT = 'customer_support',
  SALES = 'sales',
  TECHNICAL_SUPPORT = 'technical_support',
  EDUCATION = 'education',
  HEALTHCARE = 'healthcare',
  VOICEBOT = 'voicebot',
  CHATBOT = 'chatbot',
  WARM_TRANSFER = 'warm_transfer',
}

export interface PromptTemplate {
  id: string;
  customerId: string | null;
  name: string;
  nameI18n?: Record<string, string>;
  description: string | null;
  descriptionI18n?: Record<string, string>;
  category: PromptTemplateCategory;
  promptContent: string;
  promptContentI18n?: Record<string, string>;
  availableLanguages?: string[];
  missingLanguages?: string[];
  isFullyTranslated?: boolean;
  isSystemTemplate: boolean;
  isActive: boolean;
  iconUrl?: string | null;
  createdBy: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreatePromptTemplateRequest {
  name?: string;
  nameI18n?: Record<string, string>;
  description?: string;
  descriptionI18n?: Record<string, string>;
  category: PromptTemplateCategory;
  promptContent?: string;
  promptContentI18n?: Record<string, string>;
  isActive?: boolean;
}

export interface UpdatePromptTemplateRequest extends Partial<CreatePromptTemplateRequest> { }

export interface PromptTemplateListParams {
  page?: number;
  limit?: number;
  category?: PromptTemplateCategory;
  isSystemTemplate?: boolean;
  isActive?: boolean;
  search?: string;
}

export interface PromptTemplateListResponse {
  templates: PromptTemplate[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ===== SYSTEM GUIDELINES INTERFACES =====

export type SystemGuidelinesType =
  | 'chat'
  | 'voice'
  | 'warm_forwarding_briefing'
  | 'hook_condition'
  | 'assistant_wizard'
  | 'prompt_check'
  | 'prompt_optimize'
  | 'conversation_analysis'
  | 'scenario_test'
  | 'qa_scenario_suggest'
  | 'evaluation_classification';

/** Standard-Auswertung des qualitativen Reportings, gepflegt im Admin-Panel (CA-762). */
export interface StandardEvaluationCriterion {
  id: string;
  /** Stabiler Schlüssel, z.B. `anliegen`. */
  systemKey: string | null;
  name: string;
  /** Auswertungslogik — Transkript, Kategorien und Format ergänzt der Hintergrund-Prompt. */
  prompt: string;
  categories: string[];
  /** Globaler Schalter für alle Bots. */
  isActive: boolean;
  showInConversationList: boolean;
  sortOrder: number;
  updatedAt: string;
}

/** Ein Hook, der eine Auswertung in seinen Bedingungen nutzt (409-Antwort, CA-663). */
export interface CriterionHookUsage {
  hookId: string;
  hookName: string;
  agentId: string;
  agentName: string;
  customerId: string;
  customerName: string;
  /** Referenzierte Kategorien der Auswertung. */
  categories: string[];
}

/** 409-Body, wenn eine Änderung Hooks brechen würde (CA-663). */
export interface CriterionInUseError extends ApiError {
  code: 'EVALUATION_CRITERION_IN_USE';
  /** Strukturierte 4xx-Daten (einziges Feld neben `code`, das der Gateway-Filter durchreicht). */
  details: { usages: CriterionHookUsage[] };
}

/** Änderung einer Standard-Auswertung (CA-762); gilt ab dem nächsten ausgewerteten Gespräch. */
export interface UpdateStandardEvaluationCriterionRequest {
  name?: string;
  prompt?: string;
  categories?: string[];
  isActive?: boolean;
}

export interface SystemGuideline {
  id: string;
  agentType: SystemGuidelinesType;
  name: string;
  description?: string;
  content: string;
  isActive: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSystemGuidelineRequest {
  agentType: SystemGuidelinesType;
  name: string;
  description?: string;
  content: string;
}

export interface UpdateSystemGuidelineRequest {
  name?: string;
  description?: string;
  content?: string;
}

// ===== PROMPT-GUIDELINE MANIFEST / VALIDATION / PREVIEW (CA-427) =====
// Spiegel der Backend-Typen aus @callbert-ai/shared
// (constants/prompt-guideline-manifest.ts). Speist den strukturierten
// Editor: Platzhalter-Palette, gesperrte Vertrags-Abschnitte, Varianten-Tabs.

/** Ein dokumentierter Platzhalter eines Guideline-Typs (Palette + Validierung). */
export interface PromptPlaceholderDoc {
  name: string;
  description: string;
  required: boolean;
}

/** Eine erlaubte Ausprägung einer Varianten-Gruppe (`{{#IF KEY=value}}`). */
export interface PromptVariantValue {
  value: string;
  label: string;
  required: boolean;
}

/** Eine Varianten-Gruppe: erlaubter IF-Schlüssel + seine Ausprägungen. */
export interface PromptVariantGroup {
  key: string;
  label: string;
  values: PromptVariantValue[];
}

/** Manifest-Eintrag eines Guideline-Typs. */
export interface PromptGuidelineManifestEntry {
  placeholders: PromptPlaceholderDoc[];
  variantGroups: PromptVariantGroup[];
  requiredSubstrings: string[];
  lockedHeadings: string[];
  fullyLocked?: boolean;
}

/** Manifest je Guideline-Typ; Typen ohne Eintrag sind freie Inhalte. */
export type PromptGuidelineManifest = Partial<Record<SystemGuidelinesType, PromptGuidelineManifestEntry>>;

/** Ergebnis der Content-Validierung (Fehler blockieren Speichern/Aktivieren). */
export interface PromptGuidelineValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

/** Gerenderte Prompt-Vorschau. */
export interface PromptGuidelinePreviewResult {
  rendered: string;
  warnings: string[];
}

// ===== CONTACT REASON INTERFACES =====

export interface ContactReason {
  id: string;
  templateId: string;
  name: string;
  description?: string;
  promptSnippet: string;
  nameI18n?: Record<string, string>;
  descriptionI18n?: Record<string, string>;
  promptSnippetI18n?: Record<string, string>;
  availableLanguages?: string[];
  missingLanguages?: string[];
  isFullyTranslated?: boolean;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  template?: PromptTemplate;
}

export interface CreateContactReasonRequest {
  templateId: string;
  name: string;
  description?: string;
  promptSnippet: string;
  nameI18n?: Record<string, string>;
  descriptionI18n?: Record<string, string>;
  promptSnippetI18n?: Record<string, string>;
  sortOrder?: number;
  isActive?: boolean;
}

export interface UpdateContactReasonRequest {
  name?: string;
  description?: string;
  promptSnippet?: string;
  nameI18n?: Record<string, string>;
  descriptionI18n?: Record<string, string>;
  promptSnippetI18n?: Record<string, string>;
  sortOrder?: number;
  isActive?: boolean;
}

export interface ReorderContactReasonsRequest {
  orderedIds: string[];
}

// ===== SPEECH-TO-TEXT PROVIDER INTERFACES =====

export type LocalizedText = string | Record<string, string>;

export interface SttConfigFieldOption {
  value: string | number | boolean;
  label: LocalizedText;
  hint?: LocalizedText;
  group?: string;
}

export interface SttModelConfigField {
  type: 'string' | 'textarea' | 'number' | 'boolean' | 'select' | 'slider' | 'list';
  label: LocalizedText;
  description?: LocalizedText;
  default?: string | number | boolean | string[];
  options?: SttConfigFieldOption[];
  min?: number;
  max?: number;
  step?: number;
  maxLength?: number;
  order?: number;
  group?: string;
}

export type SttModelConfigSchema = Record<string, SttModelConfigField>;

export interface SttModel {
  name: string;
  displayName: LocalizedText;
  description?: LocalizedText;
  ranking?: number;
  configSchema?: SttModelConfigSchema;
  /** Estimated cost per minute in EUR (used by admin cost-overview). */
  pricePerMinuteEur?: number;
}

export type SttProviderType = 'deepgram' | 'openai' | 'assemblyai' | 'soniox' | 'custom';

export interface SpeechToTextProvider {
  id: string;
  name: string;
  displayName: string;
  providerType: SttProviderType;
  baseUrl?: string;
  apiKeyRequired: boolean;
  models: SttModel[];
  configSchema: Record<string, unknown>;
  status: 'active' | 'inactive' | 'deprecated';
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateSpeechToTextProviderRequest {
  name: string;
  displayName: string;
  providerType: SttProviderType;
  baseUrl?: string;
  apiKeyRequired?: boolean;
  models?: SttModel[];
  configSchema?: Record<string, unknown>;
  status?: 'active' | 'inactive' | 'deprecated';
}

export interface UpdateSpeechToTextProviderRequest extends Partial<CreateSpeechToTextProviderRequest> { }

// ===== TEXT-TO-SPEECH PROVIDER INTERFACES =====

export interface TtsVoice {
  id: string;
  name: string;
  language?: string;
  gender?: string;
  category?: string;
  description?: string;
  preview_url?: string;
  preview_text?: string;
}

export interface TtsModel {
  name: string;
  displayName: string;
  description?: string;
  ranking?: number;
  /** Estimated cost per minute in EUR (used by admin cost-overview). */
  pricePerMinuteEur?: number;
}

export interface TextToSpeechProvider {
  id: string;
  name: string;
  displayName: string;
  providerType: 'elevenlabs' | 'azure' | 'google' | 'custom';
  baseUrl?: string;
  apiKeyRequired: boolean;
  voices: TtsVoice[];
  models: TtsModel[];
  configSchema: Record<string, any>;
  hasDefaultApiKey: boolean;
  status: 'active' | 'inactive' | 'deprecated';
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateTextToSpeechProviderRequest {
  name: string;
  displayName: string;
  providerType: 'elevenlabs' | 'azure' | 'google' | 'custom';
  baseUrl?: string;
  apiKeyRequired?: boolean;
  voices?: TtsVoice[];
  models?: TtsModel[];
  configSchema?: Record<string, any>;
  defaultApiKey?: string;
  status?: 'active' | 'inactive' | 'deprecated';
}

export interface UpdateTextToSpeechProviderRequest extends Partial<CreateTextToSpeechProviderRequest> { }

export interface VoicePreviewRequest {
  provider: string;
  voiceId: string;
  text: string;
  config?: Record<string, any>;
}

export interface VoicePreviewResponse {
  audioData: string; // Base64 encoded audio with data URL prefix
  mimeType: string;
  size: number;
  duration?: number;
}

export interface CustomerTtsProvider {
  id: string;
  customerId: string;
  providerId: string;
  config: Record<string, any>;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  provider?: TextToSpeechProvider;
}

export interface AssignTtsProviderRequest {
  providerId: string;
  apiKey?: string;
  config?: Record<string, any>;
  isActive?: boolean;
}

export interface UpdateCustomerTtsProviderRequest {
  apiKey?: string;
  config?: Record<string, any>;
  isActive?: boolean;
}

export interface CustomerSttProvider {
  id: string;
  customerId: string;
  providerId: string;
  config: Record<string, any>;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  provider?: SpeechToTextProvider;
}

export interface AssignSttProviderRequest {
  providerId: string;
  apiKey?: string;
  config?: Record<string, any>;
  isActive?: boolean;
}

export interface UpdateCustomerSttProviderRequest {
  apiKey?: string;
  config?: Record<string, any>;
  isActive?: boolean;
}

// ===== HOOK PROVIDER INTERFACES =====

export interface HookProvider {
  id: string;
  name: string;
  description?: string;
  type: 'email' | 'sms';
  status: 'active' | 'inactive';
  isDefault: boolean;
  config: {
    smtp?: {
      host: string;
      port: number;
      secure?: boolean;
      auth: {
        user: string;
        pass: string;
      };
      from: string;
      replyTo?: string;
      tls?: {
        rejectUnauthorized?: boolean;
      };
    };
    provider?: string;
    sms?: {
      apiKey: string;
      gateway?: string;
      originator?: string;
    };
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface EmailHookConfig {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  password: string;
  from: string;
  replyTo?: string;
}

export interface SMSHookConfig {
  apiUrl: string;
  apiKey: string;
  senderId?: string;
  additionalParams?: Record<string, any>;
}

export interface CreateHookProviderRequest {
  name: string;
  description?: string;
  type: 'email' | 'sms';
  config: {
    smtp?: {
      host: string;
      port: number;
      secure?: boolean;
      auth: {
        user: string;
        pass: string;
      };
      from: string;
      replyTo?: string;
      tls?: {
        rejectUnauthorized?: boolean;
      };
    };
    provider?: string;
    sms?: {
      apiKey: string;
      gateway?: string;
      originator?: string;
    };
  };
  isDefault?: boolean;
}

export interface UpdateHookProviderRequest {
  name?: string;
  description?: string;
  config?: {
    smtp?: {
      host: string;
      port: number;
      secure?: boolean;
      auth: {
        user: string;
        pass: string;
      };
      from: string;
      replyTo?: string;
      tls?: {
        rejectUnauthorized?: boolean;
      };
    };
    provider?: string;
    sms?: {
      apiKey: string;
      gateway?: string;
      originator?: string;
    };
  };
  status?: 'active' | 'inactive';
  isDefault?: boolean;
}

export interface HookProviderListResponse {
  items: HookProvider[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface HookProviderListParams {
  page?: number;
  limit?: number;
  type?: 'email' | 'sms';
  status?: 'active' | 'inactive';
  search?: string;
}

export interface TestHookProviderRequest {
  type: 'email' | 'sms';
  config: {
    smtp?: {
      host: string;
      port: number;
      secure?: boolean;
      auth: {
        user: string;
        pass: string;
      };
      from: string;
      replyTo?: string;
      tls?: {
        rejectUnauthorized?: boolean;
      };
    };
    provider?: string;
    api?: {
      apiKey: string;
      gateway?: string;
      originator?: string;
    };
  };
  recipient: string;
  subject?: string;
  content: string;
}

export interface TestHookProviderResponse {
  success: boolean;
  message: string;
  details?: any;
}

// ===== TOOL ACCESS MANAGEMENT INTERFACES =====

export interface ToolFunction {
  id: string;
  toolDefinitionId: string;
  name: string;
  displayName: string;
  description: string;
  schema: any;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ToolDefinition {
  id: string;
  name: string;
  displayName: string;
  description: string;
  category: 'function' | 'api' | 'webhook' | 'mcp_server';
  version: string;
  status: 'active' | 'inactive' | 'deprecated';
  schema: any;
  configuration: Record<string, any>;
  executionConfig: Record<string, any>;
  tags: string[];
  author?: string;
  documentation?: string;
  iconUrl?: string;
  baseUrl?: string; // Base URL for API tools
  isPublic: boolean;
  fillerEnabled: boolean; // Enable filler responses during tool execution
  /** Opt-in: evaluate the spec body `example` to detect caller-defined field names (import-time only). */
  detectDynamicBodyFields: boolean;
  authStrategy: 'none' | 'admin' | 'customer';
  customerAuthType?: string;
  customerAuthHeaderName?: string;
  customerAuthDescription?: string;
  usesFunctions: boolean;
  sourceSchema?: Record<string, any>;
  functions?: ToolFunction[];
  createdAt: Date;
  updatedAt: Date;
  usageCount?: number; // omitted by the backend for tools without executions yet
  agentCount: number;
  lastUsedAt?: Date;
}

export interface ToolAccessInfo {
  toolDefinitionId: string;
  customerId: string;
  hasAccess: boolean;
  isPublic: boolean;
  grantedAt?: Date;
  expiresAt?: Date;
  grantedBy?: string;
}

export interface GrantToolAccessRequest {
  customerId: string;
  expiresAt?: Date;
}

export interface RevokeToolAccessRequest {
  customerId: string;
}

export interface FunctionAccessInfo {
  toolFunctionId: string;
  toolDefinitionId: string;
  customerId: string;
  hasAccess: boolean;
  functionName: string;
  functionDisplayName: string;
  grantedAt?: Date;
  expiresAt?: Date;
  grantedBy?: string;
}

export interface CustomerToolFunctionAccessSummary {
  toolDefinitionId: string;
  toolDisplayName: string;
  usesFunctions: boolean;
  totalFunctions: number;
  grantedFunctions: number;
  hasFunctionLevelRestrictions: boolean;
  functions: FunctionAccessInfo[];
}

export interface BulkUpdateFunctionAccessRequest {
  grantedFunctionIds: string[];
}

export interface ToolCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
}

export interface CreateToolDefinitionRequest {
  name: string;
  displayName: string;
  description: string;
  category: 'function' | 'api' | 'webhook' | 'mcp_server';
  version?: string;
  schema: any;
  configuration?: Record<string, any>;
  executionConfig?: Record<string, any>;
  tags?: string[];
  author?: string;
  documentation?: string;
  iconUrl?: string;
  baseUrl?: string;
  isPublic?: boolean;
  fillerEnabled?: boolean;
  detectDynamicBodyFields?: boolean;
  authStrategy?: 'none' | 'admin' | 'customer';
  customerAuthType?: string;
  customerAuthHeaderName?: string;
  customerAuthDescription?: string;
}

export interface UpdateToolDefinitionRequest {
  displayName?: string;
  description?: string;
  status?: 'active' | 'inactive' | 'deprecated';
  schema?: any;
  configuration?: Record<string, any>;
  executionConfig?: Record<string, any>;
  tags?: string[];
  documentation?: string;
  iconUrl?: string;
  baseUrl?: string;
  isPublic?: boolean;
  fillerEnabled?: boolean;
  detectDynamicBodyFields?: boolean;
  authStrategy?: 'none' | 'admin' | 'customer';
  customerAuthType?: string;
  customerAuthHeaderName?: string;
  customerAuthDescription?: string;
}

// ===== TOOL AUTHENTICATION CONFIG INTERFACES =====

export enum AuthType {
  HEADER = 'header',
  BEARER = 'bearer',
  API_KEY = 'api_key',
  BASIC_AUTH = 'basic_auth',
  CUSTOM = 'custom'
}

export interface ToolAuthConfig {
  id: string;
  toolDefinitionId: string;
  customerId?: string;
  actorId?: string;
  authType: AuthType;
  headerName: string;
  description?: string;
  isActive: boolean;
  scope: 'global' | 'customer' | 'user';
  createdAt: Date;
  updatedAt: Date;
  toolDefinition?: {
    id: string;
    name: string;
    displayName: string;
  };
  customer?: {
    id: string;
    name: string;
  };
  actor?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface CreateToolAuthConfigRequest {
  toolDefinitionId: string;
  customerId?: string;
  actorId?: string;
  authType: AuthType;
  headerName: string;
  headerValue: string;
  description?: string;
  isActive?: boolean;
}

export interface UpdateToolAuthConfigRequest {
  authType?: AuthType;
  headerName?: string;
  headerValue?: string;
  description?: string;
  isActive?: boolean;
}

export interface ToolAuthConfigQueryParams {
  toolDefinitionId?: string;
  customerId?: string;
  actorId?: string;
  authType?: AuthType;
  scope?: 'global' | 'customer' | 'user';
  isActive?: boolean;
  limit?: number;
  offset?: number;
}

export interface ToolAuthConfigListResponse {
  configs: ToolAuthConfig[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuthTypeInfo {
  id: string;
  name: string;
  description: string;
  example: string;
}

export interface ToolAuthConfigStats {
  totalConfigs: number;
  authTypeBreakdown: Record<string, number>;
  scopeBreakdown: Record<string, number>;
  statusBreakdown: {
    active: number;
    inactive: number;
  };
  configsWithCustomScope: number;
  configsWithUserScope: number;
  globalConfigs: number;
}

// ===== MCP SERVER INTERFACES =====

export type McpServerStatus = 'active' | 'inactive' | 'error' | 'connecting';
export type McpTransportType = 'sse' | 'streamable_http';

export interface McpDiscoveredTool {
  name: string;
  description: string;
  inputSchema: {
    type: string;
    properties: Record<string, unknown>;
    required?: string[];
  };
}

export interface McpServerConfig {
  id: string;
  name: string;
  description?: string;
  serverUrl: string;
  transportType: McpTransportType;
  status: McpServerStatus;
  lastConnectedAt?: string;
  lastError?: string;
  discoveredToolsCount: number;
  isActive: boolean;
  discoveredTools?: McpDiscoveredTool[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateMcpServerRequest {
  name: string;
  description?: string;
  serverUrl: string;
  transportType: McpTransportType;
  isActive?: boolean;
}

export interface UpdateMcpServerRequest {
  name?: string;
  description?: string;
  serverUrl?: string;
  transportType?: McpTransportType;
  isActive?: boolean;
}

export interface McpSyncResult {
  serverName: string;
  toolsDiscovered: number;
  tools: McpDiscoveredTool[];
  errors: string[];
}

export interface McpOAuthStatus {
  connected: boolean;
  connectedAt?: string;
  expiresAt?: number;
  scopesGranted?: string[];
  requiresReauth?: boolean;
}

// ===== SUMMARY PROMPT COLLECTION INTERFACES =====

export interface SummaryFieldConfig {
  prompt: string;
}

export interface SummaryFields {
  customerIntent: SummaryFieldConfig;
  issueResolved: SummaryFieldConfig;
  providedSolution: SummaryFieldConfig;
  keywords: SummaryFieldConfig;
  sentiment: SummaryFieldConfig;
  conversationSummary: SummaryFieldConfig;
  shortSummary: SummaryFieldConfig;
}

export interface SummaryPromptCollection {
  id: string;
  name: string;
  description?: string;
  isSystemDefault: boolean;
  isActive: boolean;
  fields: SummaryFields;
  enabledFieldsCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface SummaryCollectionListResponse {
  items: SummaryPromptCollection[];
  total: number;
  page: number;
  limit: number;
}

export interface CreateSummaryCollectionRequest {
  name: string;
  description?: string;
  isSystemDefault?: boolean;
  isActive?: boolean;
  fields: SummaryFields;
}

export interface UpdateSummaryCollectionRequest {
  name?: string;
  description?: string;
  isActive?: boolean;
  fields?: SummaryFields;
}

// Field definitions with type info for the UI
export const SUMMARY_FIELD_DEFINITIONS: Record<
  keyof SummaryFields,
  { type: string; label: string }
> = {
  customerIntent: { type: 'string', label: 'Customer Intent' },
  issueResolved: { type: 'boolean', label: 'Issue Resolved' },
  providedSolution: { type: 'string', label: 'Provided Solution' },
  keywords: { type: 'string[]', label: 'Keywords' },
  sentiment: { type: 'enum', label: 'Sentiment' },
  conversationSummary: { type: 'string', label: 'Conversation Summary' },
  shortSummary: { type: 'string', label: 'Short Summary' },
};

// Default field values for new collections
export const DEFAULT_SUMMARY_FIELDS: SummaryFields = {
  customerIntent: { prompt: 'Brief description of what the customer wanted to achieve (1-2 sentences)' },
  issueResolved: { prompt: 'Whether the customers issue or request was resolved during this conversation' },
  providedSolution: { prompt: 'Description of the solution, advice, or assistance provided' },
  keywords: { prompt: '3-5 relevant keywords or topics from this conversation' },
  sentiment: { prompt: 'Overall emotional tone of the conversation (positive, neutral, or negative)' },
  conversationSummary: { prompt: 'Concise narrative summary of the conversation in 2-3 sentences' },
  shortSummary: { prompt: 'Ultra-short summary of the conversation in max 160 characters, suitable for SMS notifications' },
};

// ===== REGISTRATION PROFILE INTERFACES =====

export interface PlatformConfig {
  showChatbots: boolean;
  showVoicebots: boolean;
  showKnowledge: boolean;
  showTemplates: boolean;
  showTools: boolean;
  showTestScenarios: boolean;
  showUserManagement: boolean;
  showVoiceFunctionality: boolean;
  showSpeechToText: boolean;
  showTimeoutsConfig: boolean;
  showFeedbackFunctionality: boolean;
  showApiKeysManagement: boolean;
}

export interface RegistrationProfileModel {
  id?: string;
  modelId: string;
  isEnabled?: boolean;
  /** If true, this model is the default LLM for new customers created via this profile. */
  isDefault?: boolean;
  defaultMonthlyTokenLimit?: number;
  defaultMonthlyRequestLimit?: number;
  defaultMaxMonthlyCost?: number;
  defaultPermissions?: {
    canUseTools?: boolean;
    canStream?: boolean;
    maxTemperature?: number;
    maxTokensPerRequest?: number;
    allowedFeatures?: string[];
    restrictedFeatures?: string[];
  };
  model?: {
    id: string;
    modelKey: string;
    displayName: string;
    provider: {
      name: string;
      displayName: string;
    };
  };
}

export interface RegistrationProfileSttProvider {
  id?: string;
  providerId: string;
  isEnabled?: boolean;
  isDefault?: boolean;
  defaultConfig?: Record<string, any>;
  provider?: {
    id: string;
    name: string;
    displayName: string;
  };
}

export interface RegistrationProfileTtsProvider {
  id?: string;
  providerId: string;
  isEnabled?: boolean;
  isDefault?: boolean;
  defaultConfig?: Record<string, any>;
  provider?: {
    id: string;
    name: string;
    displayName: string;
  };
}

export interface RegistrationProfileTemplate {
  id?: string;
  templateId: string;
  isEnabled?: boolean;
  isAutoApplied?: boolean;
  sortOrder?: number;
  template?: {
    id: string;
    name: string;
    description: string;
    category: string;
  };
}

export interface RegistrationProfile {
  id: string;
  name: string;
  description?: string;
  subscriptionPlanId: string;
  platformConfig: PlatformConfig;
  isActive: boolean;
  isDefault: boolean;
  sortOrder: number;
  createdBy?: string;
  createdAt: Date;
  updatedAt: Date;
  subscriptionPlan?: {
    id: string;
    name: string;
    description: string;
    billingPeriod: string;
    priceInCents: number;
    currency: string;
  };
  creator?: {
    id: string;
    name: string;
    email: string;
  };
  profileModels?: RegistrationProfileModel[];
  profileSttProviders?: RegistrationProfileSttProvider[];
  profileTtsProviders?: RegistrationProfileTtsProvider[];
  profileTemplates?: RegistrationProfileTemplate[];
}

export interface CreateRegistrationProfileRequest {
  name: string;
  description?: string;
  subscriptionPlanId: string;
  platformConfig: PlatformConfig;
  isActive?: boolean;
  isDefault?: boolean;
  sortOrder?: number;
  models?: {
    modelId: string;
    isEnabled?: boolean;
    /** Mark as default LLM for customers registered via this profile. */
    isDefault?: boolean;
    defaultMonthlyTokenLimit?: number;
    defaultMonthlyRequestLimit?: number;
    defaultMaxMonthlyCost?: number;
    defaultPermissions?: Record<string, any>;
  }[];
  sttProviders?: {
    providerId: string;
    isEnabled?: boolean;
    isDefault?: boolean;
    defaultConfig?: Record<string, any>;
  }[];
  ttsProviders?: {
    providerId: string;
    isEnabled?: boolean;
    isDefault?: boolean;
    defaultConfig?: Record<string, any>;
  }[];
  templates?: {
    templateId: string;
    isEnabled?: boolean;
    isAutoApplied?: boolean;
    sortOrder?: number;
  }[];
}

export interface UpdateRegistrationProfileRequest {
  name?: string;
  description?: string;
  subscriptionPlanId?: string;
  platformConfig?: PlatformConfig;
  isActive?: boolean;
  isDefault?: boolean;
  sortOrder?: number;
}

export interface RegistrationProfileQueryParams {
  page?: number;
  limit?: number;
  isActive?: boolean;
  isDefault?: boolean;
  search?: string;
}

export interface RegistrationProfileListResponse {
  profiles: RegistrationProfile[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ApplyRegistrationProfileRequest {
  customerId: string;
  overrideExisting?: boolean;
}

// ===== PLAN CAPABILITIES TYPES =====

/** A feature that is enabled for a subscription plan, returned by the capabilities endpoint. */
export interface PlanCapabilityFeature {
  name: string;
  displayName: string;
  description?: string;
}

/** A tool that is enabled and active for a subscription plan, returned by the capabilities endpoint. */
export interface PlanCapabilityTool {
  toolDefinitionId: string;
  name: string;
  displayName: string;
  /** Tool category enum value, e.g. 'function', 'api', 'webhook', 'mcp_server'. */
  category: string;
}

/** Read-only snapshot of which features and tools are activated for a given plan. */
export interface PlanCapabilitiesResponse {
  planId: string;
  features: PlanCapabilityFeature[];
  tools: PlanCapabilityTool[];
}

// ===== PLAN-TOOL WHITELIST TYPES (CA-358) =====

export interface AdminToolDefinition {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  category: string;
  status: string;
  usageContext?: string;
  /** When true, this tool is platform-managed (e.g. set_send_hook) and bypasses plan-gating. Hidden from plan-tools picker. */
  isSystemManaged?: boolean;
}

export interface SubscriptionPlanToolListItem {
  id: string;
  planId: string;
  toolDefinitionId: string;
  isEnabled: boolean;
  defaultConfig?: Record<string, unknown> | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  toolName: string;
  toolDisplayName: string;
  toolCategory: string;
  toolStatus: string;
  toolDescription?: string;
}

// ===== FEATURE-FLAG TYPES (CA-380) =====

export interface AdminFeature {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  isActive: boolean;
  sortIx: number;
  createdAt: string;
  updatedAt: string;
}

export interface PlanFeatureListItem {
  id: string;
  planId: string;
  featureId: string;
  isEnabled: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  featureName: string;
  featureDisplayName: string;
  featureDescription?: string;
}

export interface CustomerFeatureListItem {
  id: string;
  customerId: string;
  featureId: string;
  expiresAt: string | null;
  grantedByAdminId: string | null;
  notes?: string | null;
  grantedAt: string;
  featureName: string;
  featureDisplayName: string;
  featureDescription?: string;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  description?: string;
  billingPeriod: 'weekly' | 'monthly' | 'yearly';
  priceInCents: number;
  priceInEuros?: number;
  currency: string;
  pricePerMinuteInCents: number;
  pricePerMinuteInEuros?: number;
  pricePerChatInCents: number;
  pricePerChatInEuros?: number;
  maxChatbots?: number | null;
  maxVoicebots?: number | null;
  maxCallMinutes?: number | null;
  freeInclusiveMinutes?: number | null;
  maxConversations?: number | null;
  maxTestRuns?: number | null;
  maxPhoneNumbers?: number | null;
  maxKnowledgeDirectories?: number | null;
  includedPhoneNumbers?: number | null;
  phoneSetupFeeInCents: number;
  phoneSetupFeeInEuros?: number;
  phoneMonthlyFeeInCents: number;
  phoneMonthlyFeeInEuros?: number;
  externalForwardingEnabled: boolean;
  pricePerExternalForwardingInCents: number;
  pricePerExternalForwardingInEuros?: number;
  maxExternalForwardings?: number | null;
  sortIx: number;
  isActive: boolean;
  isPrivate: boolean;
  isUnlimited?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateSubscriptionPlanRequest {
  name: string;
  description?: string;
  billingPeriod: 'weekly' | 'monthly' | 'yearly';
  priceInCents: number;
  currency?: string;
  pricePerMinuteInCents?: number;
  pricePerChatInCents?: number;
  includedPhoneNumbers?: number | null;
  phoneSetupFeeInCents?: number;
  phoneMonthlyFeeInCents?: number;
  externalForwardingEnabled?: boolean;
  pricePerExternalForwardingInCents?: number;
  maxExternalForwardings?: number | null;
  maxChatbots?: number | null;
  maxVoicebots?: number | null;
  maxCallMinutes?: number | null;
  freeInclusiveMinutes?: number | null;
  maxConversations?: number | null;
  maxTestRuns?: number | null;
  maxPhoneNumbers?: number | null;
  maxKnowledgeDirectories?: number | null;
  sortIx?: number;
  isActive?: boolean;
  isPrivate?: boolean;
}

export interface UpdateSubscriptionPlanRequest {
  name?: string;
  description?: string;
  billingPeriod?: 'weekly' | 'monthly' | 'yearly';
  priceInCents?: number;
  currency?: string;
  pricePerMinuteInCents?: number;
  pricePerChatInCents?: number;
  includedPhoneNumbers?: number | null;
  phoneSetupFeeInCents?: number;
  phoneMonthlyFeeInCents?: number;
  externalForwardingEnabled?: boolean;
  pricePerExternalForwardingInCents?: number;
  maxExternalForwardings?: number | null;
  maxChatbots?: number | null;
  maxVoicebots?: number | null;
  maxCallMinutes?: number | null;
  freeInclusiveMinutes?: number | null;
  maxConversations?: number | null;
  maxTestRuns?: number | null;
  maxPhoneNumbers?: number | null;
  maxKnowledgeDirectories?: number | null;
  sortIx?: number;
  isActive?: boolean;
  isPrivate?: boolean;
}

export interface CustomerSubscription {
  id: string;
  customerId: string;
  planId: string;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  /** @deprecated Use usedCallSeconds instead. */
  usedCallMinutes: number;
  usedCallSeconds: number;
  usedConversations: number;
  usedTestRuns: number;
  baseFeeInCents: number;
  pricePerMinuteInCents: number;
  pricePerChatInCents: number;
  pricePerExternalForwardingInCents: number;
  usedExternalForwardings: number;
  phoneSetupFeeInCents: number;
  phoneMonthlyFeeInCents: number;
  includedPhoneNumbers: number;
  activePhoneNumbers: number;
  maxChatbots: number | null;
  maxVoicebots: number | null;
  maxCallMinutes: number | null;
  freeInclusiveMinutes: number | null;
  maxConversations: number | null;
  maxTestRuns: number | null;
  maxPhoneNumbers: number | null;
  maxExternalForwardings: number | null;
  maxKnowledgeDirectories: number | null;
  maxUsers: number | null;
  currency: string;
  calculatedTotalInCents: number;
  status: 'active' | 'cancelled' | 'expired';
  autoRenew: boolean;
  billingAddress?: {
    company: string;
    name: string;
    street: string;
    zip: string;
    city: string;
    country: string;
  };
  daysUntilExpiry: number;
  periodProgress: number;
  plan: SubscriptionPlan;
  createdAt: Date;
  updatedAt: Date;
}

export interface UpdateCustomerPricingRequest {
  baseFeeInCents?: number;
  pricePerMinuteInCents?: number;
  pricePerChatInCents?: number;
  pricePerExternalForwardingInCents?: number;
  freeInclusiveMinutes?: number | null;
}

export interface CreateCustomerSubscriptionRequest {
  planId: string;
  startDate?: string;
}

export interface ChangeCustomerPlanRequest {
  planId: string;
}

// ── Billing Overview ──────────────────────────────────────────────

export interface BillingOverviewItem {
  subscriptionId: string;
  customerId: string;
  customerName: string;
  planName: string;
  billingPeriod: 'weekly' | 'monthly' | 'yearly';
  status: 'active' | 'cancelled' | 'expired' | 'suspended';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  baseFeeInCents: number;
  pricePerMinuteInCents: number;
  pricePerChatInCents: number;
  currency: string;
  phoneSetupFeeInCents: number;
  phoneMonthlyFeeInCents: number;
  activePhoneNumbers: number;
  /** @deprecated Use usedCallSeconds instead. */
  usedCallMinutes: number;
  usedCallSeconds: number;
  usedConversations: number;
  usedTestRuns: number;
  pricePerExternalForwardingInCents: number;
  usedExternalForwardings: number;
  calculatedTotalInCents: number;
  daysUntilExpiry: number;
  periodProgress: number;
  billingAddress?: {
    company: string;
    name: string;
    street: string;
    zip: string;
    city: string;
    country: string;
  };
}

export interface BillingOverviewResponse {
  items: BillingOverviewItem[];
  total: number;
  page: number;
  limit: number;
}

export interface BillingOverviewParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
}

// ── Phone Number Purchases ──────────────────────────────────────────────

export interface PhoneNumberPurchase {
  id: string;
  phoneNumber: string;
  agentId: string;
  setupFeeInCents: number;
  monthlyFeeInCents: number;
  currency: string;
  purchasedAt: string;
  releasedAt?: string | null;
}

// ── Billing Period Snapshots ──────────────────────────────────────────────

export interface BillingPeriodSnapshot {
  id: string;
  periodStart: string;
  periodEnd: string;
  baseFeeInCents: number;
  pricePerMinuteInCents: number;
  pricePerChatInCents: number;
  phoneMonthlyFeeInCents: number;
  currency: string;
  /** @deprecated Use usedCallSeconds instead. */
  usedCallMinutes: number;
  usedCallSeconds: number;
  usedConversations: number;
  usedTestRuns: number;
  activePhoneNumbers: number;
  pricePerExternalForwardingInCents: number;
  usedExternalForwardings: number;
  calculatedTotalInCents: number;
  planName?: string | null;
  billingAddress?: Record<string, string> | null;
  snapshotType?: string;
  createdAt: string;
}

export interface BillingHistoryResponse {
  snapshots: BillingPeriodSnapshot[];
  currentPeriod?: BillingOverviewItem | null;
}

// ── Customer Cost Overview (admin only) ────────────────────────────────────

export interface CostCategory {
  costEur: number;
  basis: string;
  unpriced: boolean;
}

/**
 * Informativer „davon Hookevaluierung"-Anteil innerhalb der llm-Kategorie
 * (bereits in `costEur` enthalten, NICHT zusätzlich in `totalCostEur`).
 * Mirrors the backend `HookEvaluationCostDto`.
 */
export interface HookEvaluationCost {
  costEur: number;
  evaluations: number;
  /** Gesamt-Tokens (= inputTokens + cachedInputTokens + cacheWriteTokens + outputTokens). */
  tokens: number;
  /** Ungecachte Input-(Prompt-)Tokens — disjunkt zu cached/cacheWrite. */
  inputTokens: number;
  outputTokens: number;
  /** Cache-gelesene Input-Tokens — disjunkt zu `inputTokens`, KEINE Teilmenge. */
  cachedInputTokens: number;
  /** Cache-geschriebene Input-Tokens — disjunkt zu `inputTokens`. */
  cacheWriteTokens: number;
  /** EUR-Anteil der ungecachten Input-Tokens (Teil von `costEur`). */
  inputCostEur: number;
  outputCostEur: number;
  /** EUR-Anteil der cache-gelesenen Input-Tokens (Teil von `costEur`). */
  cachedInputCostEur: number;
  /** EUR-Anteil der cache-geschriebenen Input-Tokens (Teil von `costEur`). */
  cacheWriteCostEur: number;
  unpriced: boolean;
}

/**
 * Informativer „davon Evaluierung"-Anteil innerhalb der llm-Kategorie:
 * LLM-Tokens der qualitativen Klassifikation (Reporting-Auswertungen).
 * Bereits in `costEur` enthalten, NICHT zusätzlich in `totalCostEur`.
 * Mirrors the backend `EvaluationClassificationCostDto` (struktureller
 * Spiegel von {@link HookEvaluationCost}).
 */
export interface EvaluationClassificationCost {
  costEur: number;
  /** Anzahl token-verbrauchender Kriteriums-Auswertungen (Altdaten ohne usage zählen nicht). */
  evaluations: number;
  /** Gesamt-Tokens (= inputTokens + cachedInputTokens + cacheWriteTokens + outputTokens). */
  tokens: number;
  /** Ungecachte Input-(Prompt-)Tokens — disjunkt zu cached/cacheWrite. */
  inputTokens: number;
  outputTokens: number;
  /** Cache-gelesene Input-Tokens — disjunkt zu `inputTokens`, KEINE Teilmenge. */
  cachedInputTokens: number;
  /** Cache-geschriebene Input-Tokens — disjunkt zu `inputTokens`. */
  cacheWriteTokens: number;
  /** EUR-Anteil der ungecachten Input-Tokens (Teil von `costEur`). */
  inputCostEur: number;
  outputCostEur: number;
  /** EUR-Anteil der cache-gelesenen Input-Tokens (Teil von `costEur`). */
  cachedInputCostEur: number;
  /** EUR-Anteil der cache-geschriebenen Input-Tokens (Teil von `costEur`). */
  cacheWriteCostEur: number;
  unpriced: boolean;
}

/**
 * LLM-Verbrauch eines (provider, model)-Paars — pro Session zählt das gemeldete
 * Ist-Modell; `reported = false` ⇒ Zuordnung übers aktuell konfigurierte
 * Agent-Modell. Mirrors the backend `ModelLlmCostDto`.
 */
export interface ModelLlmCost {
  provider: string;
  modelKey: string;
  modelName: string;
  /** Sessions (Session-Kategorien) bzw. Calls (KI-Assistent). */
  sessions: number;
  reported: boolean;
  /** Ungecachte Input-Tokens — disjunkt zu cached/cacheWrite. */
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  cacheWriteTokens: number;
  inputCostEur: number;
  outputCostEur: number;
  cachedInputCostEur: number;
  cacheWriteCostEur: number;
  costEur: number;
  unpriced: boolean;
}

/**
 * LLM-Verbrauch eines einzelnen Agents — Untergliederung der llm-Kategorie.
 * Mirrors the backend `AgentLlmCostDto`. Sortiert nach `costEur` absteigend.
 */
export interface AgentLlmCost {
  agentId: string;
  /** Anzeigename; „Gelöschter Agent" wenn der Agent nicht mehr existiert. */
  agentName: string;
  modelKey: string;
  /** Anzeigename des Preis-Modells; Fallback auf modelKey. */
  modelName: string;
  /** Anzahl geschlossener Sessions dieses Agents im Zeitraum (inkl. Test-Sessions). */
  sessions: number;
  /** Ungecachte Input-Tokens — disjunkt zu cached/cacheWrite. */
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  cacheWriteTokens: number;
  inputCostEur: number;
  outputCostEur: number;
  cachedInputCostEur: number;
  cacheWriteCostEur: number;
  costEur: number;
  unpriced: boolean;
  /** Untergliederung nach tatsächlich genutztem (provider, model); Summe = Agent-Zeile. */
  byModel: ModelLlmCost[];
}

/**
 * „davon"-Anteil session-gebundener Hintergrund-Calls innerhalb der
 * llm-Kategorie (Summary-Generierung bzw. Hook-Content-Generierung, CA-622).
 * ANDERS als Hookevaluierung/Klassifikation ADDITIV in `costEur` eingerechnet
 * (nicht Teil der Session-Totale; stammt aus `internal_llm_usage`).
 * Mirrors the backend `SummaryGenerationCostDto` / `HookContentCostDto`
 * (strukturelle Spiegel, `calls` statt `evaluations`).
 */
export interface BackgroundContentCost {
  costEur: number;
  /** Anzahl der LLM-Calls im Zeitraum. */
  calls: number;
  /** Gesamt-Tokens (= inputTokens + cachedInputTokens + cacheWriteTokens + outputTokens). */
  tokens: number;
  /** Ungecachte Input-(Prompt-)Tokens — disjunkt zu cached/cacheWrite. */
  inputTokens: number;
  outputTokens: number;
  /** Cache-gelesene Input-Tokens — disjunkt zu `inputTokens`, KEINE Teilmenge. */
  cachedInputTokens: number;
  /** Cache-geschriebene Input-Tokens — disjunkt zu `inputTokens`. */
  cacheWriteTokens: number;
  /** EUR-Anteil der ungecachten Input-Tokens (Teil von `costEur`). */
  inputCostEur: number;
  outputCostEur: number;
  /** EUR-Anteil der cache-gelesenen Input-Tokens (Teil von `costEur`). */
  cachedInputCostEur: number;
  /** EUR-Anteil der cache-geschriebenen Input-Tokens (Teil von `costEur`). */
  cacheWriteCostEur: number;
  unpriced: boolean;
  /** Untergliederung nach (provider, model) — `sessions` zählt hier Calls; Summe = Sub-Zeile. */
  byModel: ModelLlmCost[];
}

/**
 * LLM cost category with the input-(prompt-) vs. output-(completion-)token
 * breakdown. Mirrors the backend `LlmCostCategoryDto`.
 */
export interface LlmCostCategory extends CostCategory {
  inputCostEur: number;
  outputCostEur: number;
  /** Ungecachte Input-Tokens — disjunkt zu cached/cacheWrite; Gesamt-Input = Summe aller drei. */
  inputTokens: number;
  outputTokens: number;
  /** Cache-gelesene Input-Tokens (Provider-Prompt-Cache-Reads) — disjunkt zu `inputTokens`. */
  cachedInputTokens: number;
  /** Cache-geschriebene Input-Tokens (Provider-Prompt-Cache-Writes) — disjunkt zu `inputTokens`. */
  cacheWriteTokens: number;
  /** EUR-Anteil der cache-gelesenen Input-Tokens (Teil von `costEur`). */
  cachedInputCostEur: number;
  /** EUR-Anteil der cache-geschriebenen Input-Tokens (Teil von `costEur`). */
  cacheWriteCostEur: number;
  hookEvaluation: HookEvaluationCost;
  /** Informativer „davon Evaluierung"-Anteil (qualitative Klassifikation). */
  evaluationClassification: EvaluationClassificationCost;
  /** „davon Zusammenfassung"-Anteil (additiv eingerechnet, CA-622). */
  summaryGeneration: BackgroundContentCost;
  /** „davon Hook-Inhalte"-Anteil (E-Mail/SMS-Content, additiv eingerechnet, CA-622). */
  hookContent: BackgroundContentCost;
  /** Untergliederung nach Agent (Summe = Session-Anteil der Kategorie, ohne Hintergrund-Anteile). */
  byAgent: AgentLlmCost[];
}

/** Verbrauch einer einzelnen internen KI-Funktion (source). Mirrors `InternalToolSourceUsageDto`. */
export interface InternalToolSourceUsage {
  source: string;
  /** Menschenlesbares Anzeige-Label der KI-Funktion; unbekannte Sources → „Sonstige interne KI". */
  label: string;
  calls: number;
  tokens: number;
  costEur: number;
}

/**
 * Kosten interner KI-Funktionen (KI-Einrichtungsassistent + Prompt-Optimierung,
 * CA-427) — nicht session-gebunden. Session-gebundene Hintergrund-Sources
 * (Summary-/Hook-Content-Generierung) sind AUSGESCHLOSSEN — sie stecken
 * additiv in der llm-Kategorie (CA-622). Mirrors `InternalToolCostCategoryDto`.
 */
export interface InternalToolCostCategory {
  costEur: number;
  basis: string;
  unpriced: boolean;
  inputCostEur: number;
  outputCostEur: number;
  /** Ungecachte Input-(Prompt-)Tokens — disjunkt zu cached/cacheWrite. */
  inputTokens: number;
  outputTokens: number;
  /** Cache-gelesene Input-Tokens (Provider-Prompt-Cache-Reads) — disjunkt zu `inputTokens`. */
  cachedInputTokens: number;
  /** Cache-geschriebene Input-Tokens (Provider-Prompt-Cache-Writes) — disjunkt zu `inputTokens`. */
  cacheWriteTokens: number;
  /** EUR-Anteil der cache-gelesenen Input-Tokens (Teil von `costEur`). */
  cachedInputCostEur: number;
  /** EUR-Anteil der cache-geschriebenen Input-Tokens (Teil von `costEur`). */
  cacheWriteCostEur: number;
  calls: number;
  bySource: InternalToolSourceUsage[];
  /** Aufschlüsselung nach Ist-(provider, model) aus internal_llm_usage (sessions = Calls). */
  byModel: ModelLlmCost[];
}

/**
 * QA/Evaluation cost category: eval-service run costs (USD converted with the
 * configured fixed rate) plus bot-side LLM costs of evaluation sessions.
 * Mirrors the backend `QaCostCategoryDto`.
 */
export interface QaCostCategory extends CostCategory {
  evalServiceCostEur: number;
  botLlmCostEur: number;
  /** Ungecachte Input-(Prompt-)Tokens des Bot-LLM — disjunkt zu cached/cacheWrite. */
  botLlmInputTokens: number;
  botLlmOutputTokens: number;
  /** Cache-gelesene Input-Tokens — disjunkt zu `botLlmInputTokens`. */
  botLlmCachedInputTokens: number;
  /** Cache-geschriebene Input-Tokens — disjunkt zu `botLlmInputTokens`. */
  botLlmCacheWriteTokens: number;
  /** EUR-Anteil der ungecachten Input-Tokens (Teil von `botLlmCostEur`). */
  botLlmInputCostEur: number;
  botLlmOutputCostEur: number;
  /** EUR-Anteil der cache-gelesenen Input-Tokens (Teil von `botLlmCostEur`). */
  botLlmCachedInputCostEur: number;
  /** EUR-Anteil der cache-geschriebenen Input-Tokens (Teil von `botLlmCostEur`). */
  botLlmCacheWriteCostEur: number;
  runCount: number;
  /** Bot-LLM der Evaluation-Sessions untergliedert nach Agent (inkl. byModel). */
  botLlmByAgent: AgentLlmCost[];
}

export interface CustomerCostOverview {
  customerId: string;
  customerName: string;
  from: string;
  to: string;
  currency: string;
  llm: LlmCostCategory;
  stt: CostCategory;
  tts: CostCategory;
  rag: CostCategory;
  internalTools: InternalToolCostCategory;
  qa: QaCostCategory;
  totalCostEur: number;
  estimatedRevenueEur: number | null;
  estimatedMarginEur: number | null;
  sessionCount: number;
  callMinutes: number;
  subscriptionPlanName: string | null;
  dataAvailableFrom?: string;
}

export interface RagCostEstimationConfig {
  costPerDocumentEur: number;
  costPerRetrievalEur: number;
}

export interface QaCostEstimationConfig {
  usdToEurRate: number;
}

export interface CostOverviewTotals {
  llmEur: number;
  /** Background-LLM-Jobs (Hookevaluierung) in EUR — informative Teilmenge von llmEur. */
  backgroundLlmEur: number;
  sttEur: number;
  ttsEur: number;
  ragEur: number;
  internalToolsEur: number;
  qaEur: number;
  totalCostEur: number;
  estimatedRevenueEur: number;
  estimatedMarginEur: number;
  sessionCount: number;
  callMinutes: number;
}

export type CostOverviewSortField =
  | 'customerName'
  | 'subscriptionPlanName'
  | 'llm'
  | 'backgroundLlm'
  | 'stt'
  | 'tts'
  | 'rag'
  | 'internalTools'
  | 'qa'
  | 'totalCostEur'
  | 'estimatedRevenueEur'
  | 'estimatedMarginEur'
  | 'callMinutes'
  | 'sessionCount';

export interface AllCustomersCostOverview {
  from: string;
  to: string;
  currency: string;
  items: CustomerCostOverview[];
  total: number;
  page: number;
  limit: number;
  totals: CostOverviewTotals;
}

export interface CostOverviewParams {
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
  /** Serverseitiger Namens-Filter (case-insensitive Substring). */
  search?: string;
  sortBy?: CostOverviewSortField;
  sortDirection?: 'ASC' | 'DESC';
}

// ── Monthly Billing Breakdown ──────────────────────────────────────────────

export interface PlanSegmentPricing {
  baseFeeInCents: number;
  pricePerMinuteInCents: number;
  pricePerChatInCents: number;
  phoneMonthlyFeeInCents: number;
  pricePerExternalForwardingInCents: number;
}

export interface PlanSegmentBreakdown {
  planName: string;
  from: string;
  to: string;
  days: number;
  pricing: PlanSegmentPricing;
  proRataBaseFeeInCents: number;
  voiceSessions: number;
  voiceMinutes: number;
  voiceCostInCents: number;
  chatSessions: number;
  chatCostInCents: number;
  segmentTotalInCents: number;
}

export interface PhoneNumberBreakdown {
  phoneNumber: string;
  agentId: string;
  purchasedAt: string;
  releasedAt: string | null;
  activeDaysInMonth: number;
  setupFeeInCents: number;
  proRataMonthlyFeeInCents: number;
  totalCostInCents: number;
}

export interface ExternalForwardingBreakdown {
  count: number;
  pricePerForwardingInCents: number;
  totalCostInCents: number;
}

export interface BillingBreakdownSummary {
  totalBaseFeeInCents: number;
  totalVoiceCostInCents: number;
  totalChatCostInCents: number;
  totalPhoneSetupFeesInCents: number;
  totalPhoneMonthlyFeesInCents: number;
  totalExternalForwardingCostInCents: number;
  grandTotalInCents: number;
}

export interface MonthlyBillingBreakdown {
  customerId: string;
  customerName: string;
  month: string;
  monthStart: string;
  monthEnd: string;
  daysInMonth: number;
  currency: string;
  planSegments: PlanSegmentBreakdown[];
  phoneNumbers: PhoneNumberBreakdown[];
  externalForwardings: ExternalForwardingBreakdown;
  summary: BillingBreakdownSummary;
}

// ── Webhook Templates ──────────────────────────────────────────────

export type WebhookTemplateHeaderType = 'regular' | 'auth';

export interface WebhookTemplateHeaderItem {
  key: string;
  value: string;
  type: WebhookTemplateHeaderType;
}

export interface WebhookTemplateResponse {
  id: string;
  name: string;
  description?: string;
  url?: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: WebhookTemplateHeaderItem[];
  payload?: Record<string, unknown>;
  customPrompts?: string[];
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWebhookTemplateRequest {
  name: string;
  description?: string;
  url?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: WebhookTemplateHeaderItem[];
  payload?: Record<string, unknown>;
  customPrompts?: string[];
}

export interface UpdateWebhookTemplateRequest {
  name?: string;
  description?: string;
  url?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: WebhookTemplateHeaderItem[];
  payload?: Record<string, unknown>;
  customPrompts?: string[];
}

// ===== EVENT EMAIL TEMPLATE INTERFACES =====

export interface EventEmailTemplate {
  id: string;
  templateType: string;
  language: string;
  subject: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export interface EventEmailTemplateOverview {
  templateType: string;
  languages: string[];
}

export interface CreateEventEmailTemplateRequest {
  templateType: string;
  language: string;
  subject: string;
  body: string;
}

export interface UpdateEventEmailTemplateRequest {
  subject?: string;
  body?: string;
}

export interface EventEmailTemplatePreviewRequest {
  templateType: string;
  language: string;
}

export interface EventEmailTemplatePreviewResponse {
  subject: string;
  body: string;
  error?: string;
}

// ===== ADMIN EVENTS INTERFACES =====

export interface AdminEvent {
  id: string;
  eventType: string;
  /** Kategorie für System-/Cronjob-Audits; null für kundenbezogene Events. */
  category?: string | null;
  /** null für kundenlose System-Events. */
  customerId?: string | null;
  /** null für kundenlose System-Events. */
  customerName?: string | null;
  description: string;
  details?: Record<string, any>;
  createdAt: string;
}

export interface AdminEventsResponse {
  data: AdminEvent[];
  total: number;
  page: number;
  limit: number;
}

export interface AdminEventsParams {
  page?: number;
  limit?: number;
  eventType?: string;
  category?: string;
  customerId?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  searchEventType?: string;
  searchCategory?: string;
  searchCustomerName?: string;
  searchDescription?: string;
}

export interface AdminEventTypeOption {
  type: string;
  label: string;
}

export interface AdminEventCategoryOption {
  category: string;
  label: string;
}

export interface EventNotificationRule {
  id: string;
  email: string;
  eventType: string;
  eventTypeLabel: string;
  label: string | null;
  conditionType: string | null;
  conditionValue: Record<string, number> | null;
  conditionLabel: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationRulesByEmail {
  email: string;
  rules: EventNotificationRule[];
}

export interface CreateNotificationRuleRequest {
  email: string;
  eventType: string;
  label?: string;
  conditionType?: string;
  conditionValue?: Record<string, number>;
}

export interface UpdateNotificationRuleRequest {
  label?: string;
  conditionType?: string | null;
  conditionValue?: Record<string, number> | null;
  isActive?: boolean;
}

export interface PhoneCapacityInfo {
  total: number;
  assigned: number;
  free: number;
  freePercentage: number;
}

// ===== BULK OPERATION INTERFACES =====

/** Response returned by all bulk-access batch endpoints. */
export interface BulkOperationSummary {
  customersAffected: number;
  recordsGranted: number;
  message: string;
  errors?: string[];
}

export interface BulkGrantLlmModelsRequest {
  applyToAll?: boolean;
  customerIds?: string[];
  modelIds: string[];
  options?: {
    monthlyTokenLimit?: number;
    monthlyRequestLimit?: number;
    reason?: string;
  };
}

export interface BulkSetDefaultLlmModelRequest {
  applyToAll?: boolean;
  customerIds?: string[];
  modelId: string;
}

export interface BulkAssignProvidersRequest {
  applyToAll?: boolean;
  customerIds?: string[];
  providerIds: string[];
}

/** A single agent record returned by the by-model search endpoint (B1). */
export interface AgentByModel {
  id: string;
  name: string;
  customerId: string;
  customerName: string;
  llmProvider: string;
  llmModel: string;
}

/** Paginated envelope returned by the by-model search endpoint (B1). */
export interface PagedAgentsByModel {
  items: AgentByModel[];
  total: number;
  page: number;
  limit: number;
}

/** A customer row from the default-models overview (B2). */
export interface CustomerDefaultModel {
  customerId: string;
  customerName: string;
  defaultModelId?: string;
  defaultModelKey?: string;
  defaultModelDisplayName?: string;
  providerName?: string;
}

/** Result returned by the batch change-model endpoint. */
export interface ChangeAgentModelResult {
  changed: number;
  customersGranted: number;
}

export interface BulkChangeAgentModelRequest {
  agentIds: string[];
  llmProvider: string;
  llmModel: string;
}

class ApiService {
  private baseUrl: string;
  /**
   * Access-Token ausschließlich in-memory (F-9): localStorage ist für jedes
   * Script auf der Origin lesbar — XSS könnte den Token exfiltrieren. Der
   * Refresh-Token liegt in einem httpOnly-Cookie und ist für JS unsichtbar;
   * nach einem Seiten-Reload wird das Access-Token via Cookie-Refresh
   * wiederhergestellt (automatisch beim ersten 401 in request()).
   */
  private token: string | null = null;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value: any) => void;
    reject: (error: any) => void;
  }> = [];

  // Event handlers for authentication failures
  private onTokenExpired?: () => void;
  private onAuthFailure?: () => void;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    // Migration: Tokens früherer Versionen aus localStorage entfernen.
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_refresh_token');
  }

  setToken(token: string) {
    this.token = token;
  }

  /** Aktuelles Access-Token (in-memory) — z.B. für die Session-Restlaufzeit-Anzeige. */
  getAccessToken(): string | null {
    return this.token;
  }

  clearToken() {
    this.token = null;
  }

  // Set authentication event handlers
  setAuthHandlers(onTokenExpired?: () => void, onAuthFailure?: () => void) {
    this.onTokenExpired = onTokenExpired;
    this.onAuthFailure = onAuthFailure;
  }

  // Process the failed queue when token refresh completes
  private processQueue(error: ApiError | null, token: string | null = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });

    this.failedQueue = [];
  }

  /**
   * Holt ein neues Access-Token über das httpOnly-Refresh-Cookie. Die
   * Rotation des Refresh-Tokens passiert serverseitig (Set-Cookie); das
   * Frontend sieht den Refresh-Token nie (F-9).
   */
  private async refreshAccessToken(): Promise<string> {
    try {
      const response = await fetch(`${this.baseUrl}/api/admin/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = (await response.json()) as RefreshTokenResponse;
      this.setToken(data.access_token);
      return data.access_token;
    } catch (error) {
      this.clearToken();
      throw error;
    }
  }

  /**
   * Führt den Request inkl. Auth-Header und einmaligem Token-Refresh bei 401 aus.
   * Gemeinsame Basis von {@link request} (JSON) und Blob-Downloads.
   * @returns Die erfolgreiche (2xx) Response; Body ist noch nicht gelesen.
   * @throws ApiError-Objekt bei Nicht-2xx-Antwort.
   */
  private async requestResponse(
    endpoint: string,
    options: RequestInit = {},
    isRetry: boolean = false
  ): Promise<Response> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      (headers as any).Authorization = `Bearer ${this.token}`;
    }

    // credentials: 'include' damit das httpOnly-Refresh-Cookie gesetzt/gesendet
    // wird (Cookie-Path beschränkt es auf /api/admin/auth/*).
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: 'An error occurred',
        statusCode: response.status,
      })) as ApiError;

      // Handle authentication errors. 401 = invalid/expired token (try refresh
      // or log out); 403 = permission denied for an authenticated user (just
      // propagate, do NOT log them out — calling component decides how to
      // surface the rejection).
      //
      // Login-401 (falsche Credentials) ist KEIN Sitzungsproblem und darf
      // keinen Refresh-Versuch auslösen. Für alle anderen 401s wird einmalig
      // ein Cookie-basierter Refresh versucht — das deckt auch den
      // Seiten-Reload ab (Access-Token nur in-memory, also nach Reload weg).
      const isLoginRequest = endpoint.startsWith('/api/admin/auth/login');
      if (response.status === 401 && !isRetry && !isLoginRequest) {
        if (this.isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            this.failedQueue.push({ resolve, reject });
          }).then(() => {
            // Retry the original request with new token
            return this.requestResponse(endpoint, options, true);
          });
        }

        this.isRefreshing = true;
        const hadToken = this.token !== null;

        try {
          const newToken = await this.refreshAccessToken();
          this.processQueue(null, newToken);
          this.isRefreshing = false;

          // Retry the original request with new token
          return this.requestResponse(endpoint, options, true);
        } catch (refreshError) {
          this.processQueue(error, null);
          this.isRefreshing = false;

          // Refresh fehlgeschlagen: mit vorherigem Token war die Sitzung
          // abgelaufen (onTokenExpired), ohne Token fehlte die Auth von
          // vornherein (onAuthFailure, z.B. Reload mit revoziertem Cookie).
          if (hadToken) {
            this.onTokenExpired?.();
          } else {
            this.onAuthFailure?.();
          }
          throw error;
        }
      }

      throw error;
    }

    return response;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry: boolean = false
  ): Promise<T> {
    const response = await this.requestResponse(endpoint, options, isRetry);

    // Handle responses with no content (204 No Content)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T;
    }

    // Check if response has content
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    // For non-JSON responses, return undefined
    return undefined as T;
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    return this.request<LoginResponse>('/api/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  /**
   * Logout: revoziert den Refresh-Token serverseitig und löscht das
   * httpOnly-Cookie (best-effort). Wirft keinen Fehler bei Netz-/Server-
   * Problemen — der lokale State wird unabhängig von der Antwort gecleart,
   * damit Logout immer durchgeht.
   */
  async logoutAdmin(): Promise<void> {
    try {
      await fetch(`${this.baseUrl}/api/admin/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // best-effort — Token läuft serverseitig zur Not regulär durch TTL ab.
    }
  }

  async getProfile(): Promise<AdminProfile> {
    return this.request<AdminProfile>('/api/admin/auth/profile');
  }

  async updateProfile(data: UpdateProfileRequest): Promise<AdminProfile> {
    return this.request<AdminProfile>('/api/admin/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async changePassword(data: ChangePasswordRequest): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/admin/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async get2FAStatus(): Promise<TwoFactorStatus> {
    return this.request<TwoFactorStatus>('/api/admin/auth/2fa/status');
  }

  async setup2FA(): Promise<Enable2FAResponse> {
    return this.request<Enable2FAResponse>('/api/admin/auth/2fa/setup', {
      method: 'POST',
    });
  }

  async enable2FA(token: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/admin/auth/2fa/enable', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  /**
   * Setzt die 2FA eines anderen Admin-Nutzers zurück (nur Super-Admin).
   * Das Backend deaktiviert 2FA, löscht das Secret und benachrichtigt den Nutzer per E-Mail.
   */
  async resetUserTwoFactor(userId: string): Promise<ResetTwoFactorResponse> {
    return this.request<ResetTwoFactorResponse>(
      `/api/admin/users/${encodeURIComponent(userId)}/2fa/reset`,
      { method: 'POST' },
    );
  }

  async getSystemStatus(): Promise<SystemStatus> {
    return this.request<SystemStatus>('/health/system');
  }

  async getTelephonyStatus(): Promise<TelephonyStatus> {
    return this.request<TelephonyStatus>('/health/telephony');
  }

  async getDashboardMetrics(): Promise<DashboardMetrics> {
    return this.request<DashboardMetrics>('/api/admin/metrics');
  }

  async getMetricsHistory(metric: MetricType, range: TimeRange): Promise<MetricsHistoryResponse> {
    return this.request<MetricsHistoryResponse>(`/api/admin/metrics/history?metric=${metric}&range=${range}`);
  }

  /**
   * Get monthly call/chat/combined session volume for the admin dashboard.
   * @param month Month to report on, `YYYY-MM`.
   */
  async getCallVolume(month: string): Promise<CallVolumeResponse> {
    return this.request<CallVolumeResponse>(`/api/admin/call-volume?month=${month}`);
  }

  // Customer Management Methods
  async getCustomers(params?: CustomerListParams): Promise<CustomerListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortDirection) searchParams.append('sortDirection', params.sortDirection);
    if (params?.tenantId) searchParams.append('tenantId', params.tenantId);

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/admin/customers?${queryString}` : '/api/admin/customers';

    return this.request<CustomerListResponse>(endpoint);
  }

  async getCustomer(id: string): Promise<Customer> {
    return this.request<Customer>(`/api/admin/customers/${id}`);
  }

  async exportCustomers(params?: {
    format?: 'csv' | 'xlsx';
    search?: string;
    status?: 'active' | 'inactive';
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<Blob> {
    const searchParams = new URLSearchParams();
    if (params?.format) searchParams.append('format', params.format);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortDirection) searchParams.append('sortDirection', params.sortDirection);

    const queryString = searchParams.toString();
    const endpoint = queryString
      ? `/api/admin/customers/export?${queryString}`
      : '/api/admin/customers/export';

    const url = `${this.baseUrl}${endpoint}`;
    const headers: HeadersInit = {};
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(url, { headers });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Export fehlgeschlagen' }));
      throw Object.assign(new Error(errorData.message || 'Export fehlgeschlagen'), {
        statusCode: response.status,
      });
    }

    return response.blob();
  }

  async createCustomer(data: CreateCustomerRequest): Promise<Customer> {
    return this.request<Customer>('/api/admin/customers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCustomer(id: string, data: UpdateCustomerRequest): Promise<Customer> {
    return this.request<Customer>(`/api/admin/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCustomer(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/admin/customers/${id}`, {
      method: 'DELETE',
    });
  }

  async initiateCustomerCreation(data: InitiateCustomerCreationRequest): Promise<InitiateCustomerCreationResponse> {
    return this.request<InitiateCustomerCreationResponse>('/api/admin/customers/initiate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createCustomerDirectly(data: CreateCustomerDirectlyRequest): Promise<CreateCustomerDirectlyResponse> {
    return this.request<CreateCustomerDirectlyResponse>('/api/admin/customers/create-direct', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getActiveRegistrationProfiles(): Promise<RegistrationProfile[]> {
    const response = await this.request<{ profiles: RegistrationProfile[]; total: number; page: number; limit: number }>('/api/admin/registration-profiles');
    return response.profiles;
  }

  // LLM Provider Management Methods
  async getLlmProviders(includeInactive = false): Promise<LLMProvider[]> {
    const params = includeInactive ? '?includeInactive=true' : '';
    return this.request<LLMProvider[]>(`/api/admin/providers${params}`);
  }

  async getLlmProvider(id: string): Promise<LLMProvider> {
    return this.request<LLMProvider>(`/api/admin/providers/${id}`);
  }

  async createLlmProvider(data: CreateLLMProviderRequest): Promise<LLMProvider> {
    return this.request<LLMProvider>('/api/admin/providers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLlmProvider(id: string, data: UpdateLLMProviderRequest): Promise<LLMProvider> {
    return this.request<LLMProvider>(`/api/admin/providers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLlmProvider(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/admin/providers/${id}`, {
      method: 'DELETE',
    });
  }

  // LLM Model Management Methods
  async getLlmModels(params?: ModelListParams): Promise<LLMModel[]> {
    const searchParams = new URLSearchParams();
    if (params?.includeInactive) searchParams.append('includeInactive', 'true');
    if (params?.provider) searchParams.append('provider', params.provider);

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/admin/models?${queryString}` : '/api/admin/models';

    return this.request<LLMModel[]>(endpoint);
  }

  async getLlmModel(id: string): Promise<LLMModel> {
    return this.request<LLMModel>(`/api/admin/models/${id}`);
  }

  async createLlmModel(data: CreateModelRequest): Promise<LLMModel> {
    return this.request<LLMModel>('/api/admin/models', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLlmModel(id: string, data: UpdateModelRequest): Promise<LLMModel> {
    return this.request<LLMModel>(`/api/admin/models/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLlmModel(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/admin/models/${id}`, {
      method: 'DELETE',
    });
  }

  async getModelParameters(modelId: string): Promise<{
    parameters: Record<string, LLMParameterDefinition>;
    inheritedFromProvider: string[];
    modelOverrides: string[];
    unsupported: string[];
    /** Driver-Typ des Providers (das frühere `reasoning_effort`-Gating aus CA-582 wurde in CA-625 entfernt). */
    driverType: string | null;
  }> {
    return this.request(`/api/admin/models/${modelId}/parameters`);
  }

  async validateModelParameters(modelId: string, parameters: Record<string, any>): Promise<{
    valid: boolean;
    errors: Array<{ field: string; message: string }>;
  }> {
    return this.request(`/api/admin/models/${modelId}/validate-parameters`, {
      method: 'POST',
      body: JSON.stringify(parameters),
    });
  }

  async getModelDefaultParameters(modelId: string): Promise<Record<string, any>> {
    return this.request(`/api/admin/models/${modelId}/parameters/defaults`);
  }

  async getVoiceProviders() {
    return this.request('/api/voice-providers');
  }

  // Hook Provider Management Methods
  async getHookProviders(params?: HookProviderListParams): Promise<HookProviderListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.type) searchParams.append('type', params.type);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.search) searchParams.append('search', params.search);

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/admin/hook-providers?${queryString}` : '/api/admin/hook-providers';
    return this.request<HookProviderListResponse>(endpoint);
  }

  async getHookProvider(id: string): Promise<HookProvider> {
    return this.request<HookProvider>(`/api/admin/hook-providers/${id}`);
  }

  async createHookProvider(data: CreateHookProviderRequest): Promise<HookProvider> {
    return this.request<HookProvider>('/api/admin/hook-providers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateHookProvider(id: string, data: UpdateHookProviderRequest): Promise<HookProvider> {
    return this.request<HookProvider>(`/api/admin/hook-providers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteHookProvider(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/admin/hook-providers/${id}`, {
      method: 'DELETE',
    });
  }

  async testHookProvider(data: TestHookProviderRequest): Promise<TestHookProviderResponse> {
    return this.request<TestHookProviderResponse>('/api/admin/hook-providers/test', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async setDefaultHookProvider(id: string, type: 'email' | 'sms'): Promise<HookProvider> {
    return this.request<HookProvider>(`/api/admin/hook-providers/${id}/set-default`, {
      method: 'POST',
      body: JSON.stringify({ type }),
    });
  }

  // Admin Agents
  /**
   * Lädt einen einzelnen Agent per ID (admin-only, ohne Customer-Kontext).
   * Genutzt, um aus einer Agent-ID den zugehörigen Customer für die
   * Chat-Link-Impersonation aufzulösen.
   */
  async getAgentByIdAdmin(agentId: string): Promise<AdminAgentIdentity> {
    return this.request<AdminAgentIdentity>(`/api/admin/agents/${agentId}`);
  }

  /**
   * Löst eine Rufnummer in beliebiger Schreibweise auf den zugehörigen Agent auf.
   * Genutzt für den „Go to"-Sprung per Rufnummer.
   * Antwortet mit 404, wenn keine Nummer passt, und mit 409, wenn sie mehrdeutig ist.
   */
  async getAgentByPhoneNumberAdmin(phoneNumber: string): Promise<AdminAgentIdentity> {
    const query = new URLSearchParams({ phoneNumber });
    return this.request<AdminAgentIdentity>(`/api/admin/agents/by-phone-number?${query.toString()}`);
  }

  /**
   * Lädt ein Knowledge-Verzeichnis per ID (admin-only, ohne Customer-Kontext).
   * Genutzt, um aus einem Knowledge-Link den zugehörigen Customer aufzulösen.
   */
  async getKnowledgeDirectoryByIdAdmin(directoryId: string): Promise<AdminKnowledgeDirectoryIdentity> {
    return this.request<AdminKnowledgeDirectoryIdentity>(`/api/admin/knowledge-directories/${directoryId}`);
  }

  // Admin Impersonation Methods
  async startImpersonation(customerId: string, reason?: string): Promise<ImpersonationResponse> {
    return this.request<ImpersonationResponse>(`/api/admin/impersonation/start/${customerId}`, {
      method: 'POST',
      body: JSON.stringify({
        reason: reason || `Admin impersonation for customer support`,
      }),
    });
  }

  async endImpersonation(sessionId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/admin/impersonation/end/${sessionId}`, {
      method: 'POST',
    });
  }

  async getActiveImpersonationSessions() {
    return this.request('/api/admin/impersonation/active');
  }

  async getImpersonationHistory(params?: { adminId?: string; customerId?: string; limit?: number; offset?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.adminId) searchParams.append('adminId', params.adminId);
    if (params?.customerId) searchParams.append('customerId', params.customerId);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/admin/impersonation/history?${queryString}` : '/api/admin/impersonation/history';

    return this.request(endpoint);
  }

  // Prompt Template Management Methods
  async getPromptTemplates(params?: PromptTemplateListParams): Promise<PromptTemplateListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.category) searchParams.append('category', params.category);
    if (typeof params?.isSystemTemplate === 'boolean') searchParams.append('isSystemTemplate', params.isSystemTemplate.toString());
    if (typeof params?.isActive === 'boolean') searchParams.append('isActive', params.isActive.toString());
    if (params?.search) searchParams.append('search', params.search);

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/admin/prompt-templates?${queryString}` : '/api/admin/prompt-templates';

    return this.request<PromptTemplateListResponse>(endpoint);
  }

  async getPromptTemplate(id: string): Promise<PromptTemplate> {
    return this.request<PromptTemplate>(`/api/admin/prompt-templates/${id}`);
  }

  async createPromptTemplate(data: CreatePromptTemplateRequest): Promise<PromptTemplate> {
    return this.request<PromptTemplate>('/api/admin/prompt-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createSystemTemplate(data: CreatePromptTemplateRequest): Promise<PromptTemplate> {
    return this.request<PromptTemplate>('/api/admin/prompt-templates/system', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePromptTemplate(id: string, data: UpdatePromptTemplateRequest): Promise<PromptTemplate> {
    return this.request<PromptTemplate>(`/api/admin/prompt-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePromptTemplate(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/admin/prompt-templates/${id}`, {
      method: 'DELETE',
    });
  }

  async uploadPromptTemplateIcon(id: string, file: File): Promise<PromptTemplate> {
    const url = `${this.baseUrl}/api/admin/prompt-templates/${id}/icon`;
    const formData = new FormData();
    formData.append('file', file);

    const headers: HeadersInit = {};
    if (this.token) {
      (headers as Record<string, string>).Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: 'Upload fehlgeschlagen',
        statusCode: response.status,
      }));
      throw error;
    }

    return response.json();
  }

  async deletePromptTemplateIcon(id: string): Promise<PromptTemplate> {
    return this.request<PromptTemplate>(`/api/admin/prompt-templates/${id}/icon`, {
      method: 'DELETE',
    });
  }

  async getCustomerTemplates(customerId: string, params?: PromptTemplateListParams): Promise<PromptTemplateListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.category) searchParams.append('category', params.category);
    if (typeof params?.isActive === 'boolean') searchParams.append('isActive', params.isActive.toString());
    if (params?.search) searchParams.append('search', params.search);

    const queryString = searchParams.toString();
    const endpoint = queryString
      ? `/api/admin/prompt-templates/customer/${customerId}?${queryString}`
      : `/api/admin/prompt-templates/customer/${customerId}`;

    return this.request<PromptTemplateListResponse>(endpoint);
  }

  // ===== CUSTOMER MODEL ACCESS MANAGEMENT =====

  async getCustomerModelAccess(customerId: string): Promise<CustomerModelAccess[]> {
    return this.request<CustomerModelAccess[]>(`/api/admin/models/customers/${customerId}/access`);
  }

  async grantCustomerModelAccess(customerId: string, data: GrantModelAccessRequest): Promise<CustomerModelAccess[]> {
    return this.request<CustomerModelAccess[]>(`/api/admin/models/customers/${customerId}/grant-access`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async revokeCustomerModelAccess(customerId: string, data: RevokeModelAccessRequest): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/admin/models/customers/${customerId}/revoke-access`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async grantAllModelsToCustomer(customerId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/admin/models/customers/${customerId}/grant-all`, {
      method: 'POST',
    });
  }

  async getCustomerUsageStats(customerId: string, startDate?: string, endDate?: string): Promise<CustomerUsageStats> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const queryString = params.toString();
    const endpoint = queryString
      ? `/api/admin/models/customers/${customerId}/usage?${queryString}`
      : `/api/admin/models/customers/${customerId}/usage`;

    return this.request<CustomerUsageStats>(endpoint);
  }

  // ===== TEXT-TO-SPEECH PROVIDER MANAGEMENT =====

  async getTextToSpeechProviders(): Promise<TextToSpeechProvider[]> {
    return this.request<TextToSpeechProvider[]>('/api/admin/text-to-speech/providers');
  }

  async getTextToSpeechProvider(id: string): Promise<TextToSpeechProvider> {
    return this.request<TextToSpeechProvider>(`/api/admin/text-to-speech/providers/${id}`);
  }

  async createTextToSpeechProvider(data: CreateTextToSpeechProviderRequest): Promise<TextToSpeechProvider> {
    return this.request<TextToSpeechProvider>('/api/admin/text-to-speech/providers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTextToSpeechProvider(id: string, data: UpdateTextToSpeechProviderRequest): Promise<TextToSpeechProvider> {
    return this.request<TextToSpeechProvider>(`/api/admin/text-to-speech/providers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTextToSpeechProvider(id: string): Promise<void> {
    return this.request<void>(`/api/admin/text-to-speech/providers/${id}`, {
      method: 'DELETE',
    });
  }

  async previewTextToSpeechVoice(data: VoicePreviewRequest): Promise<VoicePreviewResponse> {
    return this.request<VoicePreviewResponse>('/api/admin/text-to-speech/voice-preview', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Customer TTS Provider Assignment Management
  async getCustomerTtsProviders(customerId: string): Promise<CustomerTtsProvider[]> {
    return this.request<CustomerTtsProvider[]>(`/api/admin/text-to-speech/customers/${customerId}/providers`);
  }

  async assignTtsProviderToCustomer(customerId: string, data: AssignTtsProviderRequest): Promise<CustomerTtsProvider> {
    return this.request<CustomerTtsProvider>(`/api/admin/text-to-speech/customers/${customerId}/providers`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCustomerTtsProvider(customerId: string, providerId: string, data: UpdateCustomerTtsProviderRequest): Promise<CustomerTtsProvider> {
    return this.request<CustomerTtsProvider>(`/api/admin/text-to-speech/customers/${customerId}/providers/${providerId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async removeTtsProviderFromCustomer(customerId: string, providerId: string): Promise<void> {
    return this.request<void>(`/api/admin/text-to-speech/customers/${customerId}/providers/${providerId}`, {
      method: 'DELETE',
    });
  }

  // Customer STT Provider Assignment Management
  async getCustomerSttProviders(customerId: string): Promise<CustomerSttProvider[]> {
    return this.request<CustomerSttProvider[]>(`/api/admin/speech-to-text/customers/${customerId}/providers`);
  }

  async assignSttProviderToCustomer(customerId: string, data: AssignSttProviderRequest): Promise<CustomerSttProvider> {
    return this.request<CustomerSttProvider>(`/api/admin/speech-to-text/customers/${customerId}/providers`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCustomerSttProvider(customerId: string, providerId: string, data: UpdateCustomerSttProviderRequest): Promise<CustomerSttProvider> {
    return this.request<CustomerSttProvider>(`/api/admin/speech-to-text/customers/${customerId}/providers/${providerId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async removeSttProviderFromCustomer(customerId: string, providerId: string): Promise<void> {
    return this.request<void>(`/api/admin/speech-to-text/customers/${customerId}/providers/${providerId}`, {
      method: 'DELETE',
    });
  }

  // ===== SPEECH-TO-TEXT PROVIDER MANAGEMENT =====

  async getSpeechToTextProviders(): Promise<SpeechToTextProvider[]> {
    return this.request<SpeechToTextProvider[]>('/api/admin/speech-to-text/providers');
  }

  async getSpeechToTextProvider(id: string): Promise<SpeechToTextProvider> {
    return this.request<SpeechToTextProvider>(`/api/admin/speech-to-text/providers/${id}`);
  }

  async createSpeechToTextProvider(data: CreateSpeechToTextProviderRequest): Promise<SpeechToTextProvider> {
    return this.request<SpeechToTextProvider>('/api/admin/speech-to-text/providers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSpeechToTextProvider(id: string, data: UpdateSpeechToTextProviderRequest): Promise<SpeechToTextProvider> {
    return this.request<SpeechToTextProvider>(`/api/admin/speech-to-text/providers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSpeechToTextProvider(id: string): Promise<void> {
    return this.request<void>(`/api/admin/speech-to-text/providers/${id}`, {
      method: 'DELETE',
    });
  }

  // Pending Registrations Methods
  async getPendingRegistrations(params?: URLSearchParams): Promise<any> {
    const queryString = params ? `?${params.toString()}` : '';
    return this.request<any>(`/api/admin/pending-registrations${queryString}`);
  }

  async getPendingRegistrationStats(): Promise<any> {
    return this.request<any>('/api/admin/pending-registrations/stats/summary');
  }

  async approvePendingRegistration(id: string, data?: { password?: string }): Promise<any> {
    return this.request<any>(`/api/admin/pending-registrations/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async rejectPendingRegistration(id: string, data: { reason: string }): Promise<any> {
    return this.request<any>(`/api/admin/pending-registrations/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deletePendingRegistration(id: string): Promise<void> {
    return this.request<void>(`/api/admin/pending-registrations/${id}`, {
      method: 'DELETE',
    });
  }

  // ===== TOOL ACCESS MANAGEMENT METHODS =====

  async getToolDefinitions(): Promise<ToolDefinition[]> {
    return this.request<ToolDefinition[]>('/api/admin/tools');
  }

  async getToolDefinition(id: string): Promise<ToolDefinition> {
    return this.request<ToolDefinition>(`/api/admin/tools/${id}`);
  }

  async createToolDefinition(data: CreateToolDefinitionRequest): Promise<ToolDefinition> {
    return this.request<ToolDefinition>('/api/admin/tools', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateToolDefinition(id: string, data: UpdateToolDefinitionRequest): Promise<ToolDefinition> {
    return this.request<ToolDefinition>(`/api/admin/tools/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteToolDefinition(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/admin/tools/${id}`, {
      method: 'DELETE',
    });
  }

  async getToolCategories(): Promise<{ categories: ToolCategory[] }> {
    return this.request<{ categories: ToolCategory[] }>('/api/admin/tools/categories/list');
  }

  async getCustomerToolAccess(customerId: string): Promise<ToolAccessInfo[]> {
    return this.request<ToolAccessInfo[]>(`/api/admin/tools/customers/${customerId}/access`);
  }

  async grantToolAccess(toolId: string, data: GrantToolAccessRequest): Promise<any> {
    return this.request(`/api/admin/tools/${toolId}/access/grant`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async revokeToolAccess(toolId: string, data: RevokeToolAccessRequest): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/admin/tools/${toolId}/access/revoke`, {
      method: 'DELETE',
      body: JSON.stringify(data),
    });
  }

  async getToolAccessInfo(toolId: string, customerId: string): Promise<ToolAccessInfo> {
    return this.request<ToolAccessInfo>(`/api/admin/tools/${toolId}/access/${customerId}`);
  }

  async batchGrantToolAccess(data: {
    customerIds: string[];
    toolIds: string[];
    expiresAt?: Date;
  }): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/admin/tools/batch/grant-access', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async batchRevokeToolAccess(data: {
    customerIds: string[];
    toolIds: string[];
  }): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/admin/tools/batch/revoke-access', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Tool Function Customer Access
  async getCustomerToolFunctionAccess(toolId: string, customerId: string): Promise<CustomerToolFunctionAccessSummary> {
    return this.request<CustomerToolFunctionAccessSummary>(`/api/admin/tools/${toolId}/functions/access/${customerId}`);
  }

  async bulkUpdateFunctionAccess(toolId: string, customerId: string, data: BulkUpdateFunctionAccessRequest): Promise<CustomerToolFunctionAccessSummary> {
    return this.request<CustomerToolFunctionAccessSummary>(`/api/admin/tools/${toolId}/functions/access/${customerId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getToolStats(): Promise<{
    totalTools: number;
    totalUsage: number;
    totalAgents: number;
    categoryBreakdown: Record<string, number>;
    publicTools: number;
    privateTools: number;
  }> {
    return this.request('/api/admin/tools/stats/overview');
  }

  // Platform Configuration Management (admin endpoints)
  async getCustomerPlatformConfig(customerId: string): Promise<CustomerPlatformConfig> {
    return this.request<CustomerPlatformConfig>(`/api/admin/customers/${customerId}/platform-config`);
  }

  async updateCustomerPlatformConfig(customerId: string, data: UpdatePlatformConfigRequest): Promise<CustomerPlatformConfig> {
    return this.request<CustomerPlatformConfig>(`/api/admin/customers/${customerId}/platform-config`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ===== REGISTRATION PROFILE MANAGEMENT METHODS =====

  async getRegistrationProfiles(params?: RegistrationProfileQueryParams): Promise<RegistrationProfileListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (typeof params?.isActive === 'boolean') searchParams.append('isActive', params.isActive.toString());
    if (typeof params?.isDefault === 'boolean') searchParams.append('isDefault', params.isDefault.toString());
    if (params?.search) searchParams.append('search', params.search);

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/admin/registration-profiles?${queryString}` : '/api/admin/registration-profiles';

    return this.request<RegistrationProfileListResponse>(endpoint);
  }

  async getRegistrationProfile(id: string): Promise<RegistrationProfile> {
    return this.request<RegistrationProfile>(`/api/admin/registration-profiles/${id}`);
  }

  async getDefaultRegistrationProfile(): Promise<RegistrationProfile> {
    return this.request<RegistrationProfile>('/api/admin/registration-profiles/default');
  }

  async createRegistrationProfile(data: CreateRegistrationProfileRequest): Promise<RegistrationProfile> {
    return this.request<RegistrationProfile>('/api/admin/registration-profiles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRegistrationProfile(id: string, data: UpdateRegistrationProfileRequest): Promise<RegistrationProfile> {
    return this.request<RegistrationProfile>(`/api/admin/registration-profiles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateRegistrationProfileFull(id: string, data: CreateRegistrationProfileRequest): Promise<RegistrationProfile> {
    return this.request<RegistrationProfile>(`/api/admin/registration-profiles/${id}/full`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRegistrationProfile(id: string): Promise<void> {
    return this.request<void>(`/api/admin/registration-profiles/${id}`, {
      method: 'DELETE',
    });
  }

  async duplicateRegistrationProfile(id: string): Promise<RegistrationProfile> {
    return this.request<RegistrationProfile>(`/api/admin/registration-profiles/${id}/duplicate`, {
      method: 'POST',
    });
  }

  async applyRegistrationProfile(id: string, data: ApplyRegistrationProfileRequest): Promise<{ message: string; profileId: string; customerId: string }> {
    return this.request<{ message: string; profileId: string; customerId: string }>(`/api/admin/registration-profiles/${id}/apply`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getSubscriptionPlans(includeInactive: boolean = true): Promise<SubscriptionPlan[]> {
    const query = includeInactive ? '?includeInactive=true' : '';
    return this.request<SubscriptionPlan[]>(`/api/admin/subscription-plans${query}`);
  }

  async getSubscriptionPlan(planId: string): Promise<SubscriptionPlan> {
    return this.request<SubscriptionPlan>(`/api/admin/subscription-plans/${planId}`);
  }

  async createSubscriptionPlan(data: CreateSubscriptionPlanRequest): Promise<SubscriptionPlan> {
    return this.request<SubscriptionPlan>('/api/admin/subscription-plans', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSubscriptionPlan(planId: string, data: UpdateSubscriptionPlanRequest): Promise<SubscriptionPlan> {
    return this.request<SubscriptionPlan>(`/api/admin/subscription-plans/${planId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSubscriptionPlan(planId: string): Promise<void> {
    await this.request<{ message: string }>(`/api/admin/subscription-plans/${planId}`, {
      method: 'DELETE',
    });
  }

  // ===== PLAN-TOOL WHITELIST METHODS (CA-358) =====

  async listPlanTools(planId: string): Promise<SubscriptionPlanToolListItem[]> {
    return this.request<SubscriptionPlanToolListItem[]>(`/api/admin/subscription-plans/${planId}/tools`);
  }

  async addPlanTool(
    planId: string,
    dto: { toolDefinitionId: string; isEnabled?: boolean; defaultConfig?: Record<string, unknown>; sortOrder?: number },
  ): Promise<SubscriptionPlanToolListItem> {
    return this.request<SubscriptionPlanToolListItem>(`/api/admin/subscription-plans/${planId}/tools`, {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  }

  async updatePlanTool(
    planId: string,
    toolDefinitionId: string,
    patch: { isEnabled?: boolean; defaultConfig?: Record<string, unknown> | null; sortOrder?: number },
  ): Promise<SubscriptionPlanToolListItem> {
    return this.request<SubscriptionPlanToolListItem>(
      `/api/admin/subscription-plans/${planId}/tools/${toolDefinitionId}`,
      {
        method: 'PUT',
        body: JSON.stringify(patch),
      },
    );
  }

  async removePlanTool(planId: string, toolDefinitionId: string): Promise<void> {
    await this.request<void>(`/api/admin/subscription-plans/${planId}/tools/${toolDefinitionId}`, {
      method: 'DELETE',
    });
  }

  async listAvailableToolDefinitions(): Promise<AdminToolDefinition[]> {
    return this.request<AdminToolDefinition[]>(`/api/admin/tools`);
  }

  // ===== FEATURE-FLAG METHODS (CA-380) =====

  async listFeatures(): Promise<AdminFeature[]> {
    return this.request<AdminFeature[]>(`/api/admin/features`);
  }

  async createFeature(dto: {
    name: string;
    displayName: string;
    description?: string;
    isActive?: boolean;
    sortIx?: number;
  }): Promise<AdminFeature> {
    return this.request<AdminFeature>(`/api/admin/features`, {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  }

  async updateFeature(
    id: string,
    patch: { displayName?: string; description?: string; isActive?: boolean; sortIx?: number },
  ): Promise<AdminFeature> {
    return this.request<AdminFeature>(`/api/admin/features/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  }

  async deleteFeature(id: string): Promise<void> {
    await this.request<void>(`/api/admin/features/${id}`, { method: 'DELETE' });
  }

  async listPlanFeatures(planId: string): Promise<PlanFeatureListItem[]> {
    return this.request<PlanFeatureListItem[]>(
      `/api/admin/subscription-plans/${planId}/features`,
    );
  }

  async assignPlanFeature(
    planId: string,
    dto: { featureId: string; isEnabled?: boolean; sortOrder?: number },
  ): Promise<PlanFeatureListItem> {
    return this.request<PlanFeatureListItem>(
      `/api/admin/subscription-plans/${planId}/features`,
      { method: 'POST', body: JSON.stringify(dto) },
    );
  }

  async updatePlanFeature(
    planId: string,
    featureId: string,
    patch: { isEnabled?: boolean; sortOrder?: number },
  ): Promise<PlanFeatureListItem> {
    return this.request<PlanFeatureListItem>(
      `/api/admin/subscription-plans/${planId}/features/${featureId}`,
      { method: 'PUT', body: JSON.stringify(patch) },
    );
  }

  async removePlanFeature(planId: string, featureId: string): Promise<void> {
    await this.request<void>(
      `/api/admin/subscription-plans/${planId}/features/${featureId}`,
      { method: 'DELETE' },
    );
  }

  /**
   * Returns a read-only snapshot of the activated features and tools for the
   * given plan. The backend already filters to only enabled, active,
   * non-system-managed items — no client-side filtering required.
   */
  async getPlanCapabilities(planId: string): Promise<PlanCapabilitiesResponse> {
    return this.request<PlanCapabilitiesResponse>(
      `/api/admin/subscription-plans/${planId}/capabilities`,
    );
  }

  async listCustomerFeatures(customerId: string): Promise<CustomerFeatureListItem[]> {
    return this.request<CustomerFeatureListItem[]>(
      `/api/admin/customers/${customerId}/features`,
    );
  }

  async grantCustomerFeature(
    customerId: string,
    dto: { featureId: string; expiresAt?: string | null; notes?: string | null },
  ): Promise<CustomerFeatureListItem> {
    return this.request<CustomerFeatureListItem>(
      `/api/admin/customers/${customerId}/features`,
      { method: 'POST', body: JSON.stringify(dto) },
    );
  }

  async revokeCustomerFeature(customerId: string, featureId: string): Promise<void> {
    await this.request<void>(
      `/api/admin/customers/${customerId}/features/${featureId}`,
      { method: 'DELETE' },
    );
  }

  // ===== CUSTOMER SUBSCRIPTION METHODS =====

  async getCustomerSubscription(customerId: string): Promise<CustomerSubscription | null> {
    return this.request<CustomerSubscription | null>(`/api/admin/subscription-plans/customers/${customerId}/subscription`);
  }

  async createCustomerSubscription(customerId: string, data: CreateCustomerSubscriptionRequest): Promise<CustomerSubscription> {
    return this.request<CustomerSubscription>(`/api/admin/subscription-plans/customers/${customerId}/subscription`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async changeCustomerPlan(customerId: string, planId: string): Promise<CustomerSubscription> {
    return this.request<CustomerSubscription>(`/api/admin/subscription-plans/customers/${customerId}/subscription/plan`, {
      method: 'PUT',
      body: JSON.stringify({ planId }),
    });
  }

  async renewCustomerSubscription(customerId: string): Promise<CustomerSubscription> {
    return this.request<CustomerSubscription>(`/api/admin/subscription-plans/customers/${customerId}/subscription/renew`, {
      method: 'POST',
    });
  }

  async updateCustomerPricing(customerId: string, data: UpdateCustomerPricingRequest): Promise<CustomerSubscription> {
    return this.request<CustomerSubscription>(`/api/admin/subscription-plans/customers/${customerId}/subscription/pricing`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ===== BILLING OVERVIEW METHODS =====

  async getBillingOverview(params: BillingOverviewParams = {}): Promise<BillingOverviewResponse> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.search) query.set('search', params.search);
    if (params.status) query.set('status', params.status);
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortDirection) query.set('sortDirection', params.sortDirection);
    const qs = query.toString();
    return this.request<BillingOverviewResponse>(`/api/admin/subscription-plans/billing${qs ? `?${qs}` : ''}`);
  }

  async getBillingHistory(customerId: string): Promise<BillingHistoryResponse> {
    return this.request<BillingHistoryResponse>(`/api/admin/subscription-plans/billing/history/${customerId}`);
  }

  async getBillingPeriodDetail(snapshotId: string): Promise<BillingPeriodSnapshot> {
    return this.request<BillingPeriodSnapshot>(`/api/admin/subscription-plans/billing/snapshot/${snapshotId}`);
  }

  async getPhoneNumberPurchases(customerId: string): Promise<PhoneNumberPurchase[]> {
    return this.request<PhoneNumberPurchase[]>(`/api/admin/subscription-plans/billing/phone-purchases/${customerId}`);
  }

  async getMonthlyBillingBreakdown(customerId: string, year: number, month: number): Promise<MonthlyBillingBreakdown> {
    return this.request<MonthlyBillingBreakdown>(
      `/api/admin/subscription-plans/billing/breakdown/${customerId}?year=${year}&month=${month}`
    );
  }

  async getCustomerCostOverview(customerId: string, from: string, to: string): Promise<CustomerCostOverview> {
    const query = new URLSearchParams({ from, to });
    return this.request<CustomerCostOverview>(`/api/admin/customers/${customerId}/costs?${query.toString()}`);
  }

  async getAllCustomersCostOverview(params: CostOverviewParams = {}): Promise<AllCustomersCostOverview> {
    const query = new URLSearchParams();
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.search) query.set('search', params.search);
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortDirection) query.set('sortDirection', params.sortDirection);
    const qs = query.toString();
    return this.request<AllCustomersCostOverview>(`/api/admin/customers/cost-overview${qs ? `?${qs}` : ''}`);
  }

  async getRagCostEstimationConfig(): Promise<RagCostEstimationConfig> {
    return this.request<RagCostEstimationConfig>('/api/admin/system-settings/rag-cost-estimation');
  }

  async updateRagCostEstimationConfig(data: Partial<RagCostEstimationConfig>): Promise<RagCostEstimationConfig> {
    return this.request<RagCostEstimationConfig>('/api/admin/system-settings/rag-cost-estimation', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getQaCostEstimationConfig(): Promise<QaCostEstimationConfig> {
    return this.request<QaCostEstimationConfig>('/api/admin/system-settings/qa-cost-estimation');
  }

  async updateQaCostEstimationConfig(data: Partial<QaCostEstimationConfig>): Promise<QaCostEstimationConfig> {
    return this.request<QaCostEstimationConfig>('/api/admin/system-settings/qa-cost-estimation', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async suspendCustomer(customerId: string, reason?: string): Promise<{ message: string; pausedAgents: number }> {
    return this.request<{ message: string; pausedAgents: number }>(`/api/admin/customers/${customerId}/suspend`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async resumeCustomer(customerId: string): Promise<{ message: string; resumedAgents: number }> {
    return this.request<{ message: string; resumedAgents: number }>(`/api/admin/customers/${customerId}/resume`, {
      method: 'POST',
    });
  }

  // ===== TOOL AUTHENTICATION CONFIG METHODS =====

  async getToolAuthConfigs(params?: ToolAuthConfigQueryParams): Promise<ToolAuthConfigListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.toolDefinitionId) searchParams.append('toolDefinitionId', params.toolDefinitionId);
    if (params?.customerId) searchParams.append('customerId', params.customerId);
    if (params?.actorId) searchParams.append('actorId', params.actorId);
    if (params?.authType) searchParams.append('authType', params.authType);
    if (params?.scope) searchParams.append('scope', params.scope);
    if (typeof params?.isActive === 'boolean') searchParams.append('isActive', params.isActive.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/admin/tool-auth?${queryString}` : '/api/admin/tool-auth';

    return this.request<ToolAuthConfigListResponse>(endpoint);
  }

  async getToolAuthConfig(id: string): Promise<ToolAuthConfig> {
    return this.request<ToolAuthConfig>(`/api/admin/tool-auth/${id}`);
  }

  async getToolAuthConfigsByTool(toolId: string): Promise<ToolAuthConfig[]> {
    return this.request<ToolAuthConfig[]>(`/api/admin/tool-auth/tool/${toolId}`);
  }

  async createToolAuthConfig(data: CreateToolAuthConfigRequest): Promise<ToolAuthConfig> {
    return this.request<ToolAuthConfig>('/api/admin/tool-auth', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateToolAuthConfig(id: string, data: UpdateToolAuthConfigRequest): Promise<ToolAuthConfig> {
    return this.request<ToolAuthConfig>(`/api/admin/tool-auth/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteToolAuthConfig(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/admin/tool-auth/${id}`, {
      method: 'DELETE',
    });
  }

  async getAuthTypes(): Promise<{ authTypes: AuthTypeInfo[] }> {
    return this.request<{ authTypes: AuthTypeInfo[] }>('/api/admin/tool-auth/auth-types/list');
  }

  async getToolAuthConfigStats(): Promise<ToolAuthConfigStats> {
    return this.request<ToolAuthConfigStats>('/api/admin/tool-auth/stats/overview');
  }

  async resolveToolAuthHeaders(data: {
    toolDefinitionId: string;
    customerId?: string;
    actorId?: string;
  }): Promise<{
    toolDefinitionId: string;
    context: { customerId?: string; actorId?: string };
    resolvedHeaders: string[];
    headerCount: number;
    resolvedAt: Date;
  }> {
    return this.request('/api/admin/tool-auth/resolve', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async batchToggleToolAuthConfigStatus(data: {
    configIds: string[];
    isActive: boolean;
  }): Promise<{ message: string; results: any[] }> {
    return this.request('/api/admin/tool-auth/batch/toggle-status', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async batchDeleteToolAuthConfigs(data: {
    configIds: string[];
  }): Promise<{ message: string; results: any[] }> {
    return this.request('/api/admin/tool-auth/batch', {
      method: 'DELETE',
      body: JSON.stringify(data),
    });
  }

  // Summary Collections
  async getSummaryCollections(params?: {
    page?: number;
    limit?: number;
    includeInactive?: boolean;
  }): Promise<SummaryCollectionListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.includeInactive) queryParams.set('includeInactive', 'true');
    const query = queryParams.toString();
    return this.request<SummaryCollectionListResponse>(
      `/api/admin/summary-collections${query ? `?${query}` : ''}`
    );
  }

  async getSummaryCollection(id: string): Promise<SummaryPromptCollection> {
    return this.request<SummaryPromptCollection>(`/api/admin/summary-collections/${id}`);
  }

  async getDefaultSummaryCollection(): Promise<SummaryPromptCollection | null> {
    return this.request<SummaryPromptCollection | null>('/api/admin/summary-collections/default');
  }

  async createSummaryCollection(data: CreateSummaryCollectionRequest): Promise<SummaryPromptCollection> {
    return this.request<SummaryPromptCollection>('/api/admin/summary-collections', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSummaryCollection(id: string, data: UpdateSummaryCollectionRequest): Promise<SummaryPromptCollection> {
    return this.request<SummaryPromptCollection>(`/api/admin/summary-collections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async setSummaryCollectionAsDefault(id: string): Promise<SummaryPromptCollection> {
    return this.request<SummaryPromptCollection>(`/api/admin/summary-collections/${id}/default`, {
      method: 'PUT',
    });
  }

  async deleteSummaryCollection(id: string): Promise<void> {
    return this.request<void>(`/api/admin/summary-collections/${id}`, {
      method: 'DELETE',
    });
  }

  // ===== CONTACT REASON METHODS =====

  /** Standard-Auswertungen des qualitativen Reportings (CA-762). */
  async getStandardEvaluationCriteria(): Promise<StandardEvaluationCriterion[]> {
    return this.request<StandardEvaluationCriterion[]>('/api/admin/evaluation-criteria/standard');
  }

  /** Aktualisiert eine Standard-Auswertung für alle Bots (CA-762). */
  async updateStandardEvaluationCriterion(
    id: string,
    data: UpdateStandardEvaluationCriterionRequest,
  ): Promise<StandardEvaluationCriterion> {
    return this.request<StandardEvaluationCriterion>(`/api/admin/evaluation-criteria/standard/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getContactReasons(includeInactive?: boolean): Promise<ContactReason[]> {
    const searchParams = new URLSearchParams();
    if (includeInactive) searchParams.append('includeInactive', 'true');

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/admin/contact-reasons?${queryString}` : '/api/admin/contact-reasons';

    return this.request<ContactReason[]>(endpoint);
  }

  async getContactReasonsByTemplate(templateId: string, includeInactive?: boolean): Promise<ContactReason[]> {
    const searchParams = new URLSearchParams();
    if (includeInactive) searchParams.append('includeInactive', 'true');

    const queryString = searchParams.toString();
    const endpoint = queryString
      ? `/api/admin/contact-reasons/by-template/${templateId}?${queryString}`
      : `/api/admin/contact-reasons/by-template/${templateId}`;

    return this.request<ContactReason[]>(endpoint);
  }

  async getContactReason(id: string): Promise<ContactReason> {
    return this.request<ContactReason>(`/api/admin/contact-reasons/${id}`);
  }

  async createContactReason(data: CreateContactReasonRequest): Promise<ContactReason> {
    return this.request<ContactReason>('/api/admin/contact-reasons', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateContactReason(id: string, data: UpdateContactReasonRequest): Promise<ContactReason> {
    return this.request<ContactReason>(`/api/admin/contact-reasons/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteContactReason(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/admin/contact-reasons/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleContactReasonActive(id: string): Promise<ContactReason> {
    return this.request<ContactReason>(`/api/admin/contact-reasons/${id}/toggle`, {
      method: 'POST',
    });
  }

  async reorderContactReasons(templateId: string, orderedIds: string[]): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/admin/contact-reasons/reorder/${templateId}`, {
      method: 'POST',
      body: JSON.stringify({ orderedIds }),
    });
  }

  // ===== SYSTEM GUIDELINES METHODS =====

  async getSystemGuidelines(includeInactive?: boolean): Promise<SystemGuideline[]> {
    const searchParams = new URLSearchParams();
    if (includeInactive) searchParams.append('includeInactive', 'true');

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/admin/system-guidelines?${queryString}` : '/api/admin/system-guidelines';

    return this.request<SystemGuideline[]>(endpoint);
  }

  async getSystemGuideline(id: string): Promise<SystemGuideline> {
    return this.request<SystemGuideline>(`/api/admin/system-guidelines/${id}`);
  }

  async getActiveSystemGuidelineByType(type: SystemGuidelinesType): Promise<SystemGuideline | null> {
    return this.request<SystemGuideline | null>(`/api/admin/system-guidelines/active/${type}`);
  }

  async createSystemGuideline(data: CreateSystemGuidelineRequest): Promise<SystemGuideline> {
    return this.request<SystemGuideline>('/api/admin/system-guidelines', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSystemGuideline(id: string, data: UpdateSystemGuidelineRequest): Promise<SystemGuideline> {
    return this.request<SystemGuideline>(`/api/admin/system-guidelines/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async activateSystemGuideline(id: string): Promise<SystemGuideline> {
    return this.request<SystemGuideline>(`/api/admin/system-guidelines/${id}/activate`, {
      method: 'PUT',
    });
  }

  async deleteSystemGuideline(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/admin/system-guidelines/${id}`, {
      method: 'DELETE',
    });
  }

  // ===== PROMPT-GUIDELINE MANIFEST / VALIDATION / PREVIEW (CA-427) =====

  /** Manifest je Prompt-Template-Typ (Palette, Sperren, Varianten-Tabs des Editors). */
  async getSystemGuidelineManifest(): Promise<PromptGuidelineManifest> {
    const response = await this.request<{ manifest: PromptGuidelineManifest }>(
      '/api/admin/system-guidelines/manifest',
    );
    return response.manifest;
  }

  /** Ad-hoc-Validierung des Contents gegen das Manifest seines Typs (Live-Feedback). */
  async validateSystemGuideline(
    agentType: SystemGuidelinesType,
    content: string,
  ): Promise<PromptGuidelineValidationResult> {
    return this.request<PromptGuidelineValidationResult>('/api/admin/system-guidelines/validate', {
      method: 'POST',
      body: JSON.stringify({ agentType, content }),
    });
  }

  /** Rendert den Content mit Beispieldaten; `vars` steuert die Varianten-Auswahl. */
  async previewSystemGuideline(
    agentType: SystemGuidelinesType,
    content: string,
    vars?: Record<string, string>,
  ): Promise<PromptGuidelinePreviewResult> {
    return this.request<PromptGuidelinePreviewResult>('/api/admin/system-guidelines/preview', {
      method: 'POST',
      body: JSON.stringify({ agentType, content, vars }),
    });
  }

  // ===== LANGUAGE CONFIGURATION METHODS =====

  async getLanguageConfig(): Promise<LanguageConfigResponse> {
    return this.request<LanguageConfigResponse>('/api/admin/system-settings/languages');
  }

  async updateLanguageConfig(data: {
    languages?: LanguageDto[];
    defaultLanguage?: string;
  }): Promise<LanguageConfigResponse> {
    return this.request<LanguageConfigResponse>('/api/admin/system-settings/languages', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async toggleLanguage(code: string, isActive: boolean): Promise<LanguageConfigResponse> {
    return this.request<LanguageConfigResponse>('/api/admin/system-settings/languages/toggle', {
      method: 'PUT',
      body: JSON.stringify({ code, isActive }),
    });
  }

  async addCustomLanguage(code: string, label: string, isActive?: boolean): Promise<LanguageConfigResponse> {
    return this.request<LanguageConfigResponse>('/api/admin/system-settings/languages/custom', {
      method: 'POST',
      body: JSON.stringify({ code, label, isActive }),
    });
  }

  async removeCustomLanguage(code: string): Promise<LanguageConfigResponse> {
    return this.request<LanguageConfigResponse>(`/api/admin/system-settings/languages/custom/${code}`, {
      method: 'DELETE',
    });
  }

  async getTranslationSummary(): Promise<TranslationSummary> {
    return this.request<TranslationSummary>('/api/admin/prompt-templates/translation-summary');
  }

  // ── Webhook Templates ──────────────────────────────────────────────

  async getWebhookTemplates(search?: string): Promise<WebhookTemplateResponse[]> {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request<WebhookTemplateResponse[]>(`/api/admin/webhook-templates${params}`);
  }

  async getWebhookTemplate(id: string): Promise<WebhookTemplateResponse> {
    return this.request<WebhookTemplateResponse>(`/api/admin/webhook-templates/${id}`);
  }

  async createWebhookTemplate(data: CreateWebhookTemplateRequest): Promise<WebhookTemplateResponse> {
    return this.request<WebhookTemplateResponse>('/api/admin/webhook-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWebhookTemplate(id: string, data: UpdateWebhookTemplateRequest): Promise<WebhookTemplateResponse> {
    return this.request<WebhookTemplateResponse>(`/api/admin/webhook-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteWebhookTemplate(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/admin/webhook-templates/${id}`, {
      method: 'DELETE',
    });
  }

  // ===== FILLER CONFIGURATION METHODS =====

  async getFillerConfig(): Promise<FillerConfig> {
    return this.request<FillerConfig>('/api/admin/system-settings/filler');
  }

  async updateFillerConfig(data: UpdateFillerConfigRequest): Promise<FillerConfig> {
    return this.request<FillerConfig>('/api/admin/system-settings/filler', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ===== HOOK LLM CONFIGURATION METHODS (Background Job LLMs) =====

  async getHookLlmConfig(): Promise<HookLlmConfig> {
    return this.request<HookLlmConfig>('/api/admin/system-settings/hook-llm');
  }

  async updateHookLlmConfig(data: UpdateHookLlmConfigRequest): Promise<HookLlmConfig> {
    return this.request<HookLlmConfig>('/api/admin/system-settings/hook-llm', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /** Lädt die Background-Job-LLM-Konfiguration der Audio-Transkription (Whisper-Diktat). */
  async getTranscriptionLlmConfig(): Promise<TranscriptionLlmConfig> {
    return this.request<TranscriptionLlmConfig>('/api/admin/system-settings/transcription-llm');
  }

  /** Aktualisiert die Transkriptions-LLM-Konfiguration (Partial-Update). */
  async updateTranscriptionLlmConfig(data: UpdateTranscriptionLlmConfigRequest): Promise<TranscriptionLlmConfig> {
    return this.request<TranscriptionLlmConfig>('/api/admin/system-settings/transcription-llm', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getInternalAiLlmConfig(): Promise<InternalAiLlmConfig> {
    return this.request<InternalAiLlmConfig>('/api/admin/system-settings/internal-ai-llm');
  }

  async updateInternalAiLlmConfig(data: UpdateInternalAiLlmConfigRequest): Promise<InternalAiLlmConfig> {
    return this.request<InternalAiLlmConfig>('/api/admin/system-settings/internal-ai-llm', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /** Lädt die Background-Job-LLM-Konfiguration der Hook-Evaluation (CA-450). */
  async getHookEvaluationLlmConfig(): Promise<HookEvaluationLlmConfig> {
    return this.request<HookEvaluationLlmConfig>('/api/admin/system-settings/hook-evaluation-llm');
  }

  /** Aktualisiert die Hook-Evaluation-LLM-Konfiguration (Partial-Update). */
  async updateHookEvaluationLlmConfig(data: UpdateHookEvaluationLlmConfigRequest): Promise<HookEvaluationLlmConfig> {
    return this.request<HookEvaluationLlmConfig>('/api/admin/system-settings/hook-evaluation-llm', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getRateLimitDefaults(): Promise<RateLimitDefaults> {
    return this.request<RateLimitDefaults>('/api/admin/system-settings/rate-limits');
  }

  async updateRateLimitDefaults(data: UpdateRateLimitDefaultsRequest): Promise<RateLimitDefaults> {
    return this.request<RateLimitDefaults>('/api/admin/system-settings/rate-limits', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ===== ADMIN EVENTS METHODS =====

  async getAdminEvents(params?: AdminEventsParams): Promise<AdminEventsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.eventType) searchParams.append('eventType', params.eventType);
    if (params?.customerId) searchParams.append('customerId', params.customerId);
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortDirection) searchParams.append('sortDirection', params.sortDirection.toUpperCase());
    if (params?.searchEventType) searchParams.append('searchEventType', params.searchEventType);
    if (params?.searchCategory) searchParams.append('searchCategory', params.searchCategory);
    if (params?.searchCustomerName) searchParams.append('searchCustomerName', params.searchCustomerName);
    if (params?.searchDescription) searchParams.append('searchDescription', params.searchDescription);

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/admin/events?${queryString}` : '/api/admin/events';

    return this.request<AdminEventsResponse>(endpoint);
  }

  async getAdminEventTypes(): Promise<AdminEventTypeOption[]> {
    return this.request<AdminEventTypeOption[]>('/api/admin/events/types');
  }

  async getAdminEventCategories(): Promise<AdminEventCategoryOption[]> {
    return this.request<AdminEventCategoryOption[]>('/api/admin/events/categories');
  }

  // ===== EVENT EMAIL TEMPLATE METHODS =====

  async getEventEmailTemplates(filters?: {
    templateType?: string;
    language?: string;
  }): Promise<EventEmailTemplate[]> {
    const params = new URLSearchParams();
    if (filters?.templateType) params.append('templateType', filters.templateType);
    if (filters?.language) params.append('language', filters.language);
    const query = params.toString();
    const endpoint = query ? `/api/admin/event-templates?${query}` : '/api/admin/event-templates';
    return this.request<EventEmailTemplate[]>(endpoint);
  }

  async getEventEmailTemplateOverview(): Promise<EventEmailTemplateOverview[]> {
    return this.request<EventEmailTemplateOverview[]>('/api/admin/event-templates/overview');
  }

  async getEventEmailTemplate(id: string): Promise<EventEmailTemplate> {
    return this.request<EventEmailTemplate>(`/api/admin/event-templates/${id}`);
  }

  async updateEventEmailTemplate(
    id: string,
    data: UpdateEventEmailTemplateRequest,
  ): Promise<EventEmailTemplate> {
    return this.request<EventEmailTemplate>(`/api/admin/event-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ===== RATE LIMIT VIOLATION METHODS =====

  async getRateLimitViolations(params?: RateLimitViolationParams): Promise<RateLimitViolationListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.customerId) searchParams.append('customerId', params.customerId);
    if (params?.agentId) searchParams.append('agentId', params.agentId);
    if (params?.violationType) searchParams.append('violationType', params.violationType);
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());

    const queryString = searchParams.toString();
    const endpoint = queryString
      ? `/api/admin/rate-limit-violations?${queryString}`
      : '/api/admin/rate-limit-violations';

    return this.request<RateLimitViolationListResponse>(endpoint);
  }

  async getRateLimitViolationStats(params?: {
    customerId?: string;
    agentId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<RateLimitViolationStats> {
    const searchParams = new URLSearchParams();
    if (params?.customerId) searchParams.append('customerId', params.customerId);
    if (params?.agentId) searchParams.append('agentId', params.agentId);
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);

    const queryString = searchParams.toString();
    const endpoint = queryString
      ? `/api/admin/rate-limit-violations/stats?${queryString}`
      : '/api/admin/rate-limit-violations/stats';

    return this.request<RateLimitViolationStats>(endpoint);
  }

  // ===== ADMIN AGENT METHODS =====

  async getAdminAgents(params?: { customerId?: string; type?: string; hasSmsHooks?: boolean }): Promise<AdminAgent[]> {
    const searchParams = new URLSearchParams();
    if (params?.customerId) searchParams.append('customerId', params.customerId);
    if (params?.type) searchParams.append('type', params.type);
    if (params?.hasSmsHooks !== undefined) searchParams.append('hasSmsHooks', String(params.hasSmsHooks));

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/admin/agents?${queryString}` : '/api/admin/agents';

    const response = await this.request<{ data: AdminAgent[]; pagination: any }>(endpoint);
    return response.data;
  }

  /**
   * Paginated admin agent list with backend-side search (name OR agent ID substring)
   * and optional hasPhoneNumber filter. Used by the CID pool page (CA-472);
   * limit is capped at 200 by the backend.
   */
  async getAdminAgentsPaginated(params: {
    type?: string;
    search?: string;
    hasPhoneNumber?: boolean;
    page?: number;
    limit?: number;
  }): Promise<{ data: AdminAgent[]; pagination: AdminAgentsPagination }> {
    const searchParams = new URLSearchParams();
    if (params.type) searchParams.append('type', params.type);
    if (params.search) searchParams.append('search', params.search);
    if (params.hasPhoneNumber !== undefined) searchParams.append('hasPhoneNumber', String(params.hasPhoneNumber));
    if (params.page) searchParams.append('page', String(params.page));
    if (params.limit) searchParams.append('limit', String(params.limit));

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/admin/agents?${queryString}` : '/api/admin/agents';

    return this.request<{ data: AdminAgent[]; pagination: AdminAgentsPagination }>(endpoint);
  }

  async updateAgentRateLimits(agentId: string, rateLimitConfig: UpdateAgentRateLimitsRequest): Promise<AdminAgent> {
    return this.request<AdminAgent>(`/api/admin/agents/${agentId}/rate-limits`, {
      method: 'PUT',
      body: JSON.stringify({ rateLimitConfig }),
    });
  }

  async updateAgentSmsSenderId(agentId: string, smsSenderId: string | null): Promise<AdminAgent> {
    return this.request<AdminAgent>(`/api/admin/agents/${agentId}/sms-sender-id`, {
      method: 'PUT',
      body: JSON.stringify({ smsSenderId }),
    });
  }

  async createEventEmailTemplate(
    data: CreateEventEmailTemplateRequest,
  ): Promise<EventEmailTemplate> {
    return this.request<EventEmailTemplate>('/api/admin/event-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteEventEmailTemplate(id: string): Promise<void> {
    return this.request<void>(`/api/admin/event-templates/${id}`, {
      method: 'DELETE',
    });
  }

  async previewEventEmailTemplate(
    data: EventEmailTemplatePreviewRequest,
  ): Promise<EventEmailTemplatePreviewResponse> {
    return this.request<EventEmailTemplatePreviewResponse>(
      '/api/admin/event-templates/preview',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  // ============================================
  // Notification Rules
  // ============================================

  async getNotificationRules(): Promise<NotificationRulesByEmail[]> {
    return this.request<NotificationRulesByEmail[]>('/api/admin/notification-rules');
  }

  async createNotificationRule(data: CreateNotificationRuleRequest): Promise<EventNotificationRule> {
    return this.request<EventNotificationRule>('/api/admin/notification-rules', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateNotificationRule(id: string, data: UpdateNotificationRuleRequest): Promise<EventNotificationRule> {
    return this.request<EventNotificationRule>(`/api/admin/notification-rules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteNotificationRule(id: string): Promise<void> {
    await this.request<void>(`/api/admin/notification-rules/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleNotificationRule(id: string): Promise<EventNotificationRule> {
    return this.request<EventNotificationRule>(`/api/admin/notification-rules/${id}/toggle`, {
      method: 'PUT',
    });
  }

  async getPhoneCapacity(): Promise<PhoneCapacityInfo> {
    return this.request<PhoneCapacityInfo>('/api/admin/notification-rules/phone-capacity');
  }

  async setPhoneCapacity(total: number): Promise<PhoneCapacityInfo> {
    return this.request<PhoneCapacityInfo>('/api/admin/notification-rules/phone-capacity', {
      method: 'PUT',
      body: JSON.stringify({ total }),
    });
  }

  // ===== MCP SERVER METHODS =====

  async getMcpServers(): Promise<McpServerConfig[]> {
    return this.request<McpServerConfig[]>('/api/admin/mcp-servers');
  }

  async createMcpServer(data: CreateMcpServerRequest): Promise<McpServerConfig> {
    return this.request<McpServerConfig>('/api/admin/mcp-servers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMcpServer(id: string, data: UpdateMcpServerRequest): Promise<McpServerConfig> {
    return this.request<McpServerConfig>(`/api/admin/mcp-servers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteMcpServer(id: string): Promise<void> {
    return this.request<void>(`/api/admin/mcp-servers/${id}`, {
      method: 'DELETE',
    });
  }

  async syncMcpServer(id: string): Promise<McpSyncResult> {
    return this.request<McpSyncResult>(`/api/admin/mcp-servers/${id}/sync`, {
      method: 'POST',
    });
  }

  // ===== MCP OAUTH METHODS =====

  async initiateMcpOAuth(agentId: string, agentToolId: string): Promise<{ authorizationUrl: string }> {
    return this.request<{ authorizationUrl: string }>(
      `/api/tools/agents/${agentId}/mcp-tools/${agentToolId}/oauth/connect`,
      { method: 'POST' },
    );
  }

  async disconnectMcpOAuth(agentId: string, agentToolId: string): Promise<void> {
    return this.request<void>(
      `/api/tools/agents/${agentId}/mcp-tools/${agentToolId}/oauth/disconnect`,
      { method: 'POST' },
    );
  }

  async getMcpOAuthStatus(agentId: string, agentToolId: string): Promise<McpOAuthStatus> {
    return this.request<McpOAuthStatus>(
      `/api/tools/agents/${agentId}/mcp-tools/${agentToolId}/oauth/status`,
    );
  }

  // ===== CURATED INTEGRATIONS ADMIN METHODS =====

  /**
   * List all curated integrations.
   * GET /api/admin/curated-integrations
   */
  async listCuratedIntegrations(
    filter?: { status?: 'draft' | 'published' },
  ): Promise<import('../api/curated-integrations.types').CuratedIntegrationListItemDto[]> {
    const params = filter?.status ? `?status=${filter.status}` : '';
    return this.request(`/api/admin/curated-integrations${params}`);
  }

  /**
   * Get a single curated integration by ID.
   * GET /api/admin/curated-integrations/:id
   */
  async getCuratedIntegration(
    id: string,
  ): Promise<import('../api/curated-integrations.types').CuratedIntegrationListItemDto> {
    return this.request(`/api/admin/curated-integrations/${id}`);
  }

  /**
   * Create a new curated integration draft.
   * POST /api/admin/curated-integrations
   */
  async createCuratedIntegration(
    data: import('../api/curated-integrations.types').CreateCuratedIntegrationDto,
  ): Promise<import('../api/curated-integrations.types').CuratedIntegrationListItemDto> {
    return this.request('/api/admin/curated-integrations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update an existing curated integration.
   * PUT /api/admin/curated-integrations/:id
   */
  async updateCuratedIntegration(
    id: string,
    data: import('../api/curated-integrations.types').UpdateCuratedIntegrationDto,
  ): Promise<import('../api/curated-integrations.types').CuratedIntegrationListItemDto> {
    return this.request(`/api/admin/curated-integrations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete a curated integration.
   * Only allowed when connectionCount === 0 (backend returns 409 otherwise).
   * DELETE /api/admin/curated-integrations/:id
   */
  async deleteCuratedIntegration(id: string): Promise<void> {
    return this.request<void>(`/api/admin/curated-integrations/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Upload an icon image for a curated integration (multipart/form-data).
   * POST /api/admin/curated-integrations/:id/icon
   */
  async uploadCuratedIntegrationIcon(
    id: string,
    file: File,
  ): Promise<import('../api/curated-integrations.types').CuratedIntegrationListItemDto> {
    const url = `${this.baseUrl}/api/admin/curated-integrations/${id}/icon`;
    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = {};
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: 'Icon upload fehlgeschlagen',
        statusCode: response.status,
      }));
      throw error;
    }

    return response.json();
  }

  /**
   * Start the OAuth discovery flow for the admin connection of a curated integration.
   * POST /api/admin/curated-integrations/:id/discovery/start
   */
  async startCuratedIntegrationDiscovery(
    id: string,
  ): Promise<import('../api/curated-integrations.types').StartDiscoveryResponse> {
    return this.request(`/api/admin/curated-integrations/${id}/discovery/start`, {
      method: 'POST',
    });
  }

  /**
   * Get all discovered methods for a curated integration.
   * Requires admin MCP connection to be in 'connected' status.
   * GET /api/admin/curated-integrations/:id/discovered-methods
   */
  async getCuratedIntegrationDiscoveredMethods(
    id: string,
  ): Promise<import('../api/curated-integrations.types').DiscoveredMethodsResponseDto> {
    return this.request(`/api/admin/curated-integrations/${id}/discovered-methods`);
  }

  /**
   * Publish a curated integration (Draft → Published).
   * POST /api/admin/curated-integrations/:id/publish
   */
  async publishCuratedIntegration(
    id: string,
  ): Promise<import('../api/curated-integrations.types').CuratedIntegrationListItemDto> {
    return this.request(`/api/admin/curated-integrations/${id}/publish`, {
      method: 'POST',
    });
  }

  /**
   * Unpublish a curated integration (Published → Draft).
   * F2: Response includes `affectedConnectionCount` for the confirm dialog.
   * POST /api/admin/curated-integrations/:id/unpublish
   */
  async unpublishCuratedIntegration(
    id: string,
  ): Promise<import('../api/curated-integrations.types').UnpublishCuratedIntegrationResponse> {
    return this.request(`/api/admin/curated-integrations/${id}/unpublish`, {
      method: 'POST',
    });
  }

  /**
   * Trigger a re-sync of discovered methods for a curated integration.
   * Rate-limited to 1 resync/min per integration (backend returns 429).
   * POST /api/admin/curated-integrations/:id/resync
   */
  async triggerCuratedIntegrationResync(id: string): Promise<void> {
    await this.request<void>(`/api/admin/curated-integrations/${id}/resync`, {
      method: 'POST',
    });
  }

  /**
   * Get the method change log for a curated integration.
   * GET /api/admin/curated-integrations/:id/changes
   */
  async listCuratedIntegrationChanges(
    id: string,
  ): Promise<import('../api/curated-integrations.types').IntegrationChangeDto[]> {
    return this.request(`/api/admin/curated-integrations/${id}/changes`);
  }

  /**
   * Acknowledge a single method change log entry.
   * POST /api/admin/curated-integrations/:id/changes/:logId/acknowledge
   */
  async acknowledgeCuratedIntegrationChange(
    integrationId: string,
    logId: string,
  ): Promise<void> {
    await this.request<void>(
      `/api/admin/curated-integrations/${integrationId}/changes/${logId}/acknowledge`,
      { method: 'POST' },
    );
  }

  /**
   * List all active customer agent connections for a curated integration.
   * GET /api/admin/curated-integrations/:id/connections
   */
  async listCuratedIntegrationConnections(
    id: string,
  ): Promise<import('../api/curated-integrations.types').IntegrationConnectionItemDto[]> {
    return this.request(`/api/admin/curated-integrations/${id}/connections`);
  }

  /**
   * Get the notification recipient email list for curated integration changes.
   * GET /api/admin/system-settings/curated-integration-notifications
   */
  async getCuratedIntegrationNotificationRecipients(): Promise<
    import('../api/curated-integrations.types').CuratedIntegrationNotificationsResponse
  > {
    return this.request('/api/admin/system-settings/curated-integration-notifications');
  }

  /**
   * Update the notification recipient email list for curated integration changes.
   * PUT /api/admin/system-settings/curated-integration-notifications
   */
  async updateCuratedIntegrationNotificationRecipients(
    data: import('../api/curated-integrations.types').UpdateCuratedIntegrationNotificationsDto,
  ): Promise<import('../api/curated-integrations.types').CuratedIntegrationNotificationsResponse> {
    return this.request('/api/admin/system-settings/curated-integration-notifications', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ── Admin Users (Super-Admin only) ─────────────────────────────
  async getAdminUsers(): Promise<AdminUserRecord[]> {
    return this.request<AdminUserRecord[]>('/api/admin/users');
  }

  async createAdminUser(data: CreateAdminUserPayload): Promise<AdminUserRecord> {
    return this.request<AdminUserRecord>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAdminUser(id: string, data: UpdateAdminUserPayload): Promise<AdminUserRecord> {
    return this.request<AdminUserRecord>(`/api/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteAdminUser(id: string): Promise<void> {
    return this.request<void>(`/api/admin/users/${id}`, {
      method: 'DELETE',
    });
  }

  // ── CID Pool — Rufnummern-Pool (Super-Admin only) ────────────────

  /** Fetch all reserved CID pool numbers. */
  async getReservedCids(): Promise<{ phonenumbers: ReservedCidRecord[] }> {
    return this.request<{ phonenumbers: ReservedCidRecord[] }>(
      '/api/admin/telephony/cid-pool/reserved',
    );
  }

  /**
   * Bulk-import freely assignable numbers into the CID pool.
   * Already-existing numbers are returned in skipped[], not rejected.
   * @throws ApiError (400) when any number is not valid E.164.
   */
  async importCidPoolNumbers(phonenumbers: string[]): Promise<CidPoolImportResult> {
    return this.request<CidPoolImportResult>('/api/admin/telephony/cid-pool/import', {
      method: 'POST',
      body: JSON.stringify({ phonenumbers }),
    });
  }

  /**
   * Bulk-import reserved numbers with a mandatory comment.
   * Already-existing numbers are returned in skipped[], not rejected.
   * @throws ApiError (400) when any number is not valid E.164.
   */
  async importReservedCidPoolNumbers(
    phonenumbers: ImportReservedCidPayload[],
  ): Promise<CidPoolImportResult> {
    return this.request<CidPoolImportResult>('/api/admin/telephony/cid-pool/import/reserved', {
      method: 'POST',
      body: JSON.stringify({ phonenumbers }),
    });
  }

  /**
   * Assign a specific CID pool number to a bot identified by its foreign_id.
   * This is the only way to hand out reserved numbers.
   * @throws ApiError (409) when the number is already assigned to another bot.
   */
  async assignNumberToBot(foreignId: string, number: string): Promise<{ cid: string }> {
    return this.request<{ cid: string }>(
      `/api/admin/telephony/bots/${foreignId}/number`,
      {
        method: 'POST',
        body: JSON.stringify({ number }),
      },
    );
  }

  /**
   * Release the reserved CID number of a bot back into the pool.
   * The number keeps its reservation and comment; it is no longer "taken".
   * @throws ApiError (404) when no pool entry exists for this bot.
   * @throws ApiError (409) when the bot's number is not a reserved number.
   */
  async releaseReservedCid(foreignId: string): Promise<void> {
    return this.request<void>(
      `/api/admin/telephony/bots/${foreignId}/cid/reserved`,
      { method: 'DELETE' },
    );
  }

  /**
   * Release the phone number of a bot — works for pool AND reserved numbers.
   * Reserved numbers fall back reserved (with comment) into the pool.
   * @throws ApiError (404) when no pool entry exists for this bot.
   */
  async releaseAgentPhoneNumber(foreignId: string): Promise<void> {
    return this.request<void>(
      `/api/admin/telephony/bots/${foreignId}/cid`,
      { method: 'DELETE' },
    );
  }

  // ===== BULK ACCESS MANAGEMENT METHODS =====

  async bulkGrantLlmModels(body: BulkGrantLlmModelsRequest): Promise<BulkOperationSummary> {
    return this.request<BulkOperationSummary>('/api/admin/models/batch/grant-access', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async bulkSetDefaultLlmModel(body: BulkSetDefaultLlmModelRequest): Promise<BulkOperationSummary> {
    return this.request<BulkOperationSummary>('/api/admin/models/batch/set-default', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async bulkAssignTtsProviders(body: BulkAssignProvidersRequest): Promise<BulkOperationSummary> {
    return this.request<BulkOperationSummary>('/api/admin/text-to-speech/batch/assign-providers', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async bulkAssignSttProviders(body: BulkAssignProvidersRequest): Promise<BulkOperationSummary> {
    return this.request<BulkOperationSummary>('/api/admin/speech-to-text/batch/assign-providers', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // ===== AGENT MODEL REASSIGNMENT METHODS (B1) =====

  /**
   * Returns all agents currently using the given LLM provider + model key.
   * llmProvider maps to model.provider.name, llmModel maps to model.modelKey.
   * The endpoint is paginated and caps `limit` at 100 server-side, so this
   * walks every page and returns the flattened list of agents.
   */
  async getAgentsByModel(llmProvider: string, llmModel: string): Promise<AgentByModel[]> {
    const limit = 100;
    const agents: AgentByModel[] = [];
    let page = 1;
    let total = 0;
    do {
      const params = new URLSearchParams({
        llmProvider,
        llmModel,
        page: String(page),
        limit: String(limit),
      });
      const response = await this.request<PagedAgentsByModel>(
        `/api/admin/agents/by-model?${params.toString()}`
      );
      agents.push(...response.items);
      total = response.total;
      page += 1;
    } while (agents.length < total);
    return agents;
  }

  /**
   * Reassigns all given agents to a new LLM provider+model.
   * Automatically grants the target model to any customers that don't have access yet.
   */
  async bulkChangeAgentModel(body: BulkChangeAgentModelRequest): Promise<ChangeAgentModelResult> {
    return this.request<ChangeAgentModelResult>('/api/admin/agents/batch/change-model', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // ===== CUSTOMER DEFAULT MODEL OVERVIEW (B2) =====

  /**
   * Returns all customers with their current default LLM model (if set).
   */
  async getCustomersWithDefaultModel(): Promise<CustomerDefaultModel[]> {
    return this.request<CustomerDefaultModel[]>('/api/admin/models/customers/default-models');
  }

  // ── Blocked Phone Numbers (Super-Admin only) ─────────────────────
  async getBlockedPhoneNumbers(): Promise<BlockedPhoneNumberRecord[]> {
    return this.request<BlockedPhoneNumberRecord[]>('/api/admin/blocked-phone-numbers');
  }

  async createBlockedPhoneNumber(
    payload: CreateBlockedPhoneNumberPayload,
  ): Promise<BlockedPhoneNumberRecord> {
    return this.request<BlockedPhoneNumberRecord>('/api/admin/blocked-phone-numbers', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteBlockedPhoneNumber(id: string): Promise<void> {
    return this.request<void>(`/api/admin/blocked-phone-numbers/${id}`, {
      method: 'DELETE',
    });
  }

  async getAdminTenants(params: AdminTenantListParams): Promise<AdminTenantPage> {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.append('search', params.search);
    if (params.page) searchParams.append('page', String(params.page));
    if (params.limit) searchParams.append('limit', String(params.limit));
    const queryString = searchParams.toString();
    return this.request<AdminTenantPage>(queryString ? `/api/admin/tenants?${queryString}` : '/api/admin/tenants');
  }

  async createAdminTenant(payload: CreateAdminTenantPayload): Promise<AdminTenantCreated> {
    return this.request<AdminTenantCreated>('/api/admin/tenants', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async setAdminTenantStatus(tenantId: string, isActive: boolean): Promise<AdminTenant> {
    return this.request<AdminTenant>(`/api/admin/tenants/${tenantId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive }),
    });
  }

  async getAdminTenantCredentials(tenantId: string, page: number, limit: number): Promise<AdminTenantCredentialPage> {
    return this.request<AdminTenantCredentialPage>(
      `/api/admin/tenants/${tenantId}/credentials?page=${String(page)}&limit=${String(limit)}`,
    );
  }

  async createAdminTenantCredential(
    tenantId: string,
    payload: CreateAdminTenantCredentialPayload,
  ): Promise<AdminIssuedTenantCredential> {
    return this.request<AdminIssuedTenantCredential>(`/api/admin/tenants/${tenantId}/credentials`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async revokeAdminTenantCredential(tenantId: string, credentialId: string): Promise<void> {
    return this.request<void>(`/api/admin/tenants/${tenantId}/credentials/${credentialId}`, {
      method: 'DELETE',
    });
  }

  async getTenantCustomerAssignments(params: AdminTenantAssignmentListParams): Promise<AdminTenantAssignmentPage> {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.append('search', params.search);
    searchParams.append('tenantId', params.tenantId);
    if (params.page) searchParams.append('page', String(params.page));
    if (params.limit) searchParams.append('limit', String(params.limit));
    const queryString = searchParams.toString();
    return this.request<AdminTenantAssignmentPage>(
      queryString
        ? `/api/admin/tenants/customer-assignments?${queryString}`
        : '/api/admin/tenants/customer-assignments',
    );
  }

  async reassignCustomersToTenant(
    payload: ReassignCustomersToTenantPayload,
  ): Promise<ReassignCustomersToTenantResult> {
    return this.request<ReassignCustomersToTenantResult>('/api/admin/tenants/customer-assignments', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  // ── Error Events (Super-Admin only) ──────────────────────────────
  private buildErrorEventFilterParams(filter: ErrorEventFilter): URLSearchParams {
    const searchParams = new URLSearchParams();
    if (filter.service) searchParams.append('service', filter.service);
    if (filter.errorIdentifier) searchParams.append('errorIdentifier', filter.errorIdentifier);
    if (filter.customerId) searchParams.append('customerId', filter.customerId);
    if (filter.severity) searchParams.append('severity', filter.severity);
    if (filter.status) searchParams.append('status', filter.status);
    if (filter.from) searchParams.append('from', filter.from);
    if (filter.to) searchParams.append('to', filter.to);
    return searchParams;
  }

  async getErrorEvents(params?: ErrorEventListParams): Promise<ErrorEventListResponse> {
    const searchParams = this.buildErrorEventFilterParams(params ?? {});
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/admin/error-events?${queryString}` : '/api/admin/error-events';

    return this.request<ErrorEventListResponse>(endpoint);
  }

  /**
   * Lädt alle zum Filter passenden Events als CSV (Backend verlangt `from`/`to`,
   * max. 50.000 Zeilen — darüber 400 mit Hinweis zum Eingrenzen).
   * @throws ApiError-Objekt (`message`, `statusCode`) bei Nicht-2xx-Antwort.
   */
  async exportErrorEvents(filter: ErrorEventFilter): Promise<Blob> {
    const response = await this.requestResponse(
      `/api/admin/error-events/export?${this.buildErrorEventFilterParams(filter).toString()}`,
    );
    return response.blob();
  }

  async getErrorEvent(id: string): Promise<ErrorEvent> {
    return this.request<ErrorEvent>(`/api/admin/error-events/${id}`);
  }

  async acknowledgeErrorEvent(id: string): Promise<ErrorEvent> {
    return this.request<ErrorEvent>(`/api/admin/error-events/${id}/acknowledge`, {
      method: 'POST',
    });
  }

  async resolveErrorEvent(id: string): Promise<ErrorEvent> {
    return this.request<ErrorEvent>(`/api/admin/error-events/${id}/resolve`, {
      method: 'POST',
    });
  }

  async bulkAcknowledgeErrorEvents(filter: BulkErrorEventSelection): Promise<BulkErrorEventActionResponse> {
    return this.request<BulkErrorEventActionResponse>('/api/admin/error-events/bulk-acknowledge', {
      method: 'POST',
      body: JSON.stringify({ filter }),
    });
  }

  async bulkResolveErrorEvents(filter: BulkErrorEventSelection): Promise<BulkErrorEventActionResponse> {
    return this.request<BulkErrorEventActionResponse>('/api/admin/error-events/bulk-resolve', {
      method: 'POST',
      body: JSON.stringify({ filter }),
    });
  }

  async createTestErrorEvent(payload: CreateTestErrorEventRequest): Promise<CreateTestErrorEventResponse> {
    return this.request<CreateTestErrorEventResponse>('/api/admin/error-events/test', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getErrorEventNotificationSummary(): Promise<ErrorEventNotificationSummary> {
    return this.request<ErrorEventNotificationSummary>('/api/admin/error-events/summary');
  }

  // ── Error Events — Dead Letter Queue (Super-Admin only) ──────────
  async getDlqMessages(): Promise<DlqListResponse> {
    return this.request<DlqListResponse>('/api/admin/error-events/dlq');
  }

  async requeueAllDlqMessages(): Promise<DlqRequeueResponse> {
    return this.request<DlqRequeueResponse>('/api/admin/error-events/dlq/requeue', {
      method: 'POST',
    });
  }

  async requeueDlqMessage(id: string): Promise<DlqRequeueResponse> {
    return this.request<DlqRequeueResponse>(`/api/admin/error-events/dlq/${id}/requeue`, {
      method: 'POST',
    });
  }

  async deleteDlqMessage(id: string): Promise<DlqDeleteResponse> {
    return this.request<DlqDeleteResponse>(`/api/admin/error-events/dlq/${id}`, {
      method: 'DELETE',
    });
  }

  // ── Error Events — Severity-Konfiguration (Super-Admin only) ─────
  async getSeverityGroups(): Promise<SeverityGroup[]> {
    return this.request<SeverityGroup[]>('/api/admin/error-events/severity-config/groups');
  }

  async createSeverityGroup(payload: CreateSeverityGroupRequest): Promise<SeverityGroup> {
    return this.request<SeverityGroup>('/api/admin/error-events/severity-config/groups', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateSeverityGroup(id: string, payload: UpdateSeverityGroupRequest): Promise<SeverityGroup> {
    return this.request<SeverityGroup>(`/api/admin/error-events/severity-config/groups/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  /** Löscht die Gruppe; zugeordnete Identifier verlieren die Zuordnung, Kunden-Overrides der Gruppe entfallen. */
  async deleteSeverityGroup(id: string): Promise<void> {
    return this.request<void>(`/api/admin/error-events/severity-config/groups/${id}`, {
      method: 'DELETE',
    });
  }

  async getSeverityCatalog(): Promise<SeverityCatalogEntry[]> {
    return this.request<SeverityCatalogEntry[]>('/api/admin/error-events/severity-config/catalog');
  }

  /** Ordnet einen Identifier einer Gruppe zu — `null` entfernt die Zuordnung. Nicht kundenspezifisch. */
  async setSeverityCatalogGroup(errorIdentifier: string, groupId: string | null): Promise<SeverityCatalogEntry> {
    return this.request<SeverityCatalogEntry>(
      `/api/admin/error-events/severity-config/catalog/${encodeURIComponent(errorIdentifier)}/group`,
      {
        method: 'PUT',
        body: JSON.stringify({ groupId }),
      }
    );
  }

  /** Ohne `customerId` die globalen Regeln, mit `customerId` die Regeln im Kunden-Geltungsbereich. */
  async getSeverityRules(customerId?: string): Promise<SeverityRulesResponse> {
    const endpoint = customerId
      ? `/api/admin/error-events/severity-config/rules?customerId=${encodeURIComponent(customerId)}`
      : '/api/admin/error-events/severity-config/rules';
    return this.request<SeverityRulesResponse>(endpoint);
  }

  async upsertSeverityIdentifierRule(
    payload: UpsertSeverityIdentifierRuleRequest
  ): Promise<SeverityIdentifierRule> {
    return this.request<SeverityIdentifierRule>('/api/admin/error-events/severity-config/rules/identifier', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteSeverityIdentifierRule(id: string): Promise<void> {
    return this.request<void>(`/api/admin/error-events/severity-config/rules/identifier/${id}`, {
      method: 'DELETE',
    });
  }

  async upsertSeverityGroupRule(payload: UpsertSeverityGroupRuleRequest): Promise<SeverityGroupRule> {
    return this.request<SeverityGroupRule>('/api/admin/error-events/severity-config/rules/group', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteSeverityGroupRule(id: string): Promise<void> {
    return this.request<void>(`/api/admin/error-events/severity-config/rules/group/${id}`, {
      method: 'DELETE',
    });
  }
}

export const apiService = new ApiService();
